// R-025: shared browser cast gate, also used before placement and for timed finishes.
// Every applicable rule must be judged from held data. Unknown names the first unread
// input; the caller refuses before spending or drawing. No legacy rule route is used.
// Attacks use the resolved weapon with the rider under attack_ability_arr. Scene facts
// arrive through ability-preconditions-browser.ts; spell components run last.
// Historical rule/message parity: tools/test/janus-ability-preconditions-parity.test.mjs.
import { itemsCol } from '../../shared/array-types';
import { combatSizeFt, SIZE_WIDTH_FT } from '../../shared/combat-size.js';
export { combatSizeFt } from '../../shared/combat-size.js';
import { spellLockReason } from './spell-lock';
import { heldLayerlessDefinition } from '../compile/held-definition';
import { hasLiveEquipmentContext, unmetEquippedItemRequirements } from '../compile/equipped-item-requirements';
import { resolvePilotedObjectInEncounter } from '../compile/piloted-object';
import { sizeTemplates } from '../compile/from-base-static-registries.json'; // named: the default import pulls the whole 319 KB registry into every bundle (v2-98)
import { resolveFormulaArithmetic } from '../compile/formula-variable-resolution';
import { spellComponentRefusal, iconPh, statusIds, abilityBucket, layerListEntryUuid, entryId, passiveEntryActive, casterHoldsPassive, STATUS_RESTRAINED } from './spell-components';
import { isSpellGrantMode } from '../ability/spell-grant-mode';
import { throwObjectVerdict, throwObjectRangeRefusal, THROW_HAND_SLOTS, THROW_OBJECT_DRAW_AP } from './throw-object-gate';
import { HURLS_UNATTENDED_OBJECT, throwObjectMaxSizeId } from './object-handling-layers';
import { THROW_OBJECT_VARIABLE, THROW_OBJECT_ABILITY_ID } from '../../shared/throw-object-range';
import { spellManaDiscount, discountedSpellMana, nextSpellManaDiscountCarriers } from '../ability/caster-spell-layers';
import { craftingWaivesNonmagicalReagents, reagentRequirementIsMagical } from './crafting-waiver';
import { DISMISS_SUMMON, RECALL_SUMMON } from '../ability/creature-control-ids';
import { ecId, ecLean } from '../ability/context';
import { abilityActionCost, CASTING_TIME_AP } from '../ability/action-cost';
import { encounterInCombat, PSI_MECHANIC_ID } from '../economy/out-of-combat';
import { consumesUnitPerUse } from '../ability/consume-nested-unit';
import { manaReductionDelta, manaInvestmentBudget, chargeReductionDelta, chargeInvestmentBudget } from '../resources/reduction-guard';

type Row = Record<string, unknown>;

export interface AbilityPreconditionInput {
  caster: Row;
  /** Dismiss/Recall are the held controller's action; their effects still target the summon. */
  actionCaster?: Row | null;
  ability: Row;
  /** The caster's encounter blob; null when the page does not hold it (every encounter-dependent check stands down). */
  encounter: Row | null;
  /** use_ability_variable_1 / _2 exactly as the eventual cast will post them (undefined = not posted). */
  variable1?: unknown;
  /** Selected level restored from a timed Casting row; variable1 remains the finish marker. */
  castLevel?: number;
  /** A timed summon keeps its creature choice separately from the finish marker and free/charged mode. */
  summonCreatureId?: string;
  variable2?: unknown;
  /** The one-shot Quick Ritual opt-in rides this cast (its discount is server-validated, so mana stands down). */
  quickRitual?: boolean;
  /** use_weapon_inventory_id — janus swaps that weapon row in as the resolved item (the absorbed-gear gate reads it). */
  weaponInventoryId?: unknown;
  /** consume_inventory_id — the action-bar consume source row (the absorbed-gear gate reads it too). */
  consumeInventoryId?: unknown;
  consumeInventoryUnit?: true;
  /** The FT3D scene's verdicts for the two geometry rules (S4b). Absent (or a null member) = the scene could not judge. */
  sceneVerdicts?: SceneGateVerdicts | null;
  /** ft_stealth_lockout_until() + campaign_time_to_seconds() as the read-only stealth_lockout endpoint reports them for
   * this cast; `campaign: false` = the caster has no campaign context (PHP skips the rule). Absent = unknown. */
  stealthLockout?: StealthLockoutState | null;
}

/** What the scene proves for ft_stealth_hide_blocked_reason()'s watcher loop and ft_hunker_posture_unavailable_reason().
 * Each boolean is a proof; null means "a watcher / the geometry could not be judged", which stands the rule down. */
export interface SceneGateVerdicts {
  /** Cast gate (2026-09-29, R-103): the straight line of effect caster → the requested creature (a held-item target's holder). */
  lineOfEffect?: 'clear' | 'blocked' | null;
  /** Cast gate (R-103): a Burrowed area anchor's tremorsense-only line — the anchor measured at the surface above it. */
  burrowedAnchor?: 'clear' | 'blocked' | null;
  hide?: {
    /** An eligible opposing watcher inside 60 ft whose zone is OPEN (cover not closed, not deafened-and-blind). */
    zoneOpen: boolean | null;
    /** Such a watcher SEES the caster CLEARLY (ft_stealth_sense_snapshot: can_see && no light-driven Attention DIS). */
    seenClearly: boolean | null;
  } | null;
  hunker?: {
    /** ft_hunker_caster_is_aloft(): flying / hovering under its own power above its support floor. */
    aloft: boolean | null;
    /** A shaped encounter, the caster inside its union, and NO occluder within FT_HUNKER_COVER_REACH_FT. */
    noCover: boolean | null;
  } | null;
}
export interface StealthLockoutState {
  campaign: boolean;
  active: boolean;
  /** ceil((until − now) / 60) as PHP prints it; 0 when inactive. */
  minutes: number;
}

/** R-025 S5: the rule the page could not judge (the stand-down site). */
export interface AbilityGateUnknown { unknown: string }
/** The authoritative verdict: refuse, allow (every rule judged), or a named unread input. */
export type AbilityGateVerdict = { kind: 'refuse'; message: string } | { kind: 'allow' } | { kind: 'unknown'; site: string };
/** Stand-down sites that exist only because a per-cast Q2 read (ensureAbilityGateData) did not return its data. When that read
 * FAILED, the browser refuses fail-closed instead of deferring (owner Q2 = A). */
export const GATE_READ_SITES: Record<string, 'castable' | 'lockout' | 'scene' | 'names'> = {
  'innate-pool-charges': 'castable', 'stealth-lockout-read': 'lockout', 'hide-scene': 'scene', 'hunker-scene': 'scene',
  'status-required-name': 'names', 'status-forbidden-name': 'names',
};

export interface AbilityPreconditionReaders {
  /** layer_value() */
  value: (row: Row | null, key: string) => unknown;
  /** layer_has() */
  has: (row: Row | null, key: string) => boolean;
  /** creature_statuses($row, 'target'); lean references may resolve through resolveItem. */
  statuses: (row: Row) => Row[];
  /** ft_creature_in_combat() */
  inCombat: (encounter: Row, caster: Row) => boolean;
  /** layer_entries() — with resolveItem, enables the spell-component check (absent → components stand down). */
  entries?: (row: Row | null, key: string) => unknown[];
  /** inventory_field() — with resolveInventory, enables the Throw Object gate (absent → it stands down). */
  inventoryField?: (row: Row | null, key: string) => unknown;
  /** The Throw Object gate's size reads (see ThrowObjectGateReaders). */
  creatureSizeId?: (creature: Row) => unknown;
  itemSizeId?: (row: Row) => unknown;
  /** Telekinesis from where it lies (ThrowObjectGateReaders.telekineticReach, 2026-10-07); absent → a floor row must be carried. */
  telekineticReach?: (creature: Row, row: Row) => { rangeFt: number; within: boolean | null };
  /** A hurl ability's (hurls_unattended_object) picked-object refusal, null = fine (2026-10-08); absent → the rule is undecided. */
  hurlObjectRefusal?: (creature: Row, ability: Row) => string | null;
  /** An inventory id → its cached row (the thrown object); null when the page does not hold it. */
  resolveInventory?: (inventoryId: string) => Row | null;
  /** A feat entry → its full catalog def; null when the page does not hold it. */
  resolveItem?: (entry: unknown) => Row | null;
  /** get_item_id_name() for an id the catalog does not hold — the per-cast name read (R-025 S6); null when not read. */
  name?: (id: string) => string | null;
  /** Skill::ALL's {item_id, variable_name} registry — lets casterLayerProvider judge a feat's requires_skill_tier_N gate from the caster's
   *  compiled skills (absent/empty → that gate stays unprovable). */
  skillEntries?: readonly { item_id: string; variable_name: string }[];
}

const record = (value: unknown): Row | null => value !== null && typeof value === 'object' && !Array.isArray(value) ? value as Row : null;
const scalar = (value: unknown): unknown => Array.isArray(value) ? value[0] : value;
const upper = (value: unknown): string => String(value ?? '').trim().toUpperCase();
const SIZE_TEMPLATE_IDS: ReadonlySet<string> = new Set(Object.values(sizeTemplates)
  .flatMap(Object.values).filter((id): id is string => typeof id === 'string').map(upper));

/** Plain reagents accept size-fit-only stock. An explicitly templated requirement still matches exactly. */
export function reagentTemplateMatches(stockTemplate: unknown, requiredTemplate: string | null): boolean {
  const stock = upper(scalar(stockTemplate)), required = upper(requiredTemplate);
  return stock === required || required === '' && SIZE_TEMPLATE_IDS.has(stock);
}

// PHP 8 numeric-string grammar (no hex/octal/Infinity, which JS Number() would accept).
const PHP_NUMERIC_PREFIX = /^[ \t\n\r\v\f]*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?/;
const PHP_NUMERIC = /^[ \t\n\r\v\f]*[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:[eE][+-]?\d+)?[ \t\n\r\v\f]*$/;
/** PHP is_numeric() of a scalar. */
const numeric = (value: unknown): boolean => (typeof value === 'number' && Number.isFinite(value))
  || (typeof value === 'string' && PHP_NUMERIC.test(value));
/** PHP empty(). */
const phpEmpty = (value: unknown): boolean => value === undefined || value === null || value === false || value === 0
  || value === '' || value === '0' || (Array.isArray(value) && value.length === 0);
/** PHP intval() / (int) of a scalar: a leading numeric string converts as a number ("0.1e2" → 10, "12abc" → 12). */
const phpIntval = (value: unknown): number => {
  if (typeof value === 'number') return Math.trunc(value);
  if (typeof value === 'boolean') return value ? 1 : 0;
  const match = PHP_NUMERIC_PREFIX.exec(String(value ?? ''));
  return match ? Math.trunc(Number(match[0].trim())) : 0;
};

function itemField(row: Row | null, key: string): unknown {
  return scalar(record(row?.items)?.[key] ?? row?.[key]);
}
function encounterField(encounter: Row, key: string): unknown {
  const bucket = record(encounter.encounters);
  const value = bucket && key in bucket ? bucket[key] : encounter[key];
  return scalar(value);
}

// PHP's early-success modes. finish_crafting_wip is NOT one for a browser cast: its bypass needs the engine-only timed_status_row_id, so a
// posted finish falls through to the normal gates exactly as PHP does (R-025 S5).
const ACTION_SENTINELS = ['damage_only', 'end_turn_in_p_aoe', 'moving_into_p_aoe', 'read', 'finish_reading', 'unread_book', 'finish_training', 'arm_counterspell'];
const SKILL_TYPES = ['defensive_skill', 'innate_skill', 'gathering_skill', 'knowledge_skill', 'crafting_skill', 'language_skill',
  'instrument_classification_skill', 'weapon_skill', 'armor_skill'];
const OUT_OF_TURN_TYPES = [...SKILL_TYPES, 'reaction_ability', 'free_ability'];
const SPELL_LIKE = ['spell', 'curse', 'menta'];
const MANA_POOL_SKIPS = ['at_will_spell', 'innate_spell_per_long_rest', 'finish_casting_ritual_spell', 'charged_ability', 'charged_spell'];
const UUID = /^[0-9A-F]{8}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{12}$/i;
/** AbilityType::getAll() — the caster buckets the median-charge rule walks. */
const ABILITY_TYPES = ['passive_ability', 'full_ability', 'ability', 'quick_ability', 'lair_ability', 'trap_ability', 'attack_ability',
  'free_ability', 'reaction_ability', 'opportunity_attack_ability', 'curse', 'menta', 'sanguine', 'ability_choice'];
/** VitalityStatus::getAll() ids — a required / forbidden status that is a vitality tier is read from computed.vitality.v_status. */
const VITALITY_STATUS_IDS = new Set(['82788F70-EBEF-44C4-80E7-6622AD20DD60', 'FF30B76E-785D-2CAF-FC66-C131D0548808', '658483AA-34DE-74E5-C974-E3E202C31CC7',
  'C97076EF-FED9-19B4-D109-C22F9BC740E8', '44B8792E-632E-2651-C666-67DFD181E92A', '298BE1DD-2917-476E-D79C-92CF8C598926',
  'D5DE14C4-3194-87BC-D767-6D45BDD90552', '8AE129AF-52D8-CCE8-706A-85767DB52740', '9FEE2E8A-9CF7-D74D-5D16-B86C2603A1FA']);
const HAND_SLOT_IDS = new Set<string>([THROW_HAND_SLOTS.main_hand, THROW_HAND_SLOTS.off_hand, THROW_HAND_SLOTS.both_hands]);
const MELEE_WEAPON_TYPE = '1364260D-E37C-4257-AD16-5E745107811A';
const EQUIPMENT_SLOT_HEAD = '3805BDD7-3461-DDDB-F445-922A5A992987';
const WEAPON_CLASS_NATURAL = 'AB7DEAAE-62BF-4996-A510-5F2B192F8F14';
const ROLL_HEALING = '4FB7452A-F130-40C3-83E6-5E92C329D7B2';
const ROLL_REGENERATION = '9909BE44-99AE-8780-BB12-BD99BA6E672E';
const STATUS_CONCENTRATING = 'E7FDB796-26DA-E0F5-8F88-C21FE01D923B';
/** The canonical Burrowed status (MV1, owner-sanctioned 2026-09-27). */
const STATUS_BURROWED = '6436D343-5BCA-4147-8915-47A7C648977E';
const PASSIVE_DOUBLE_CONCENTRATION = '45122975-6514-4CE0-9484-A874CE211016';
const HIDE_ABILITY = 'C7326C89-CD32-C8C8-2DA9-29D66FFC2284';
const HUNKER_ABILITY = '8C28EBE3-60EA-D493-0FB8-67859CE92F63';
const END_HUNKER_ABILITY = 'CFC9BEC2-556E-E00F-7BA7-D61E8AC382D3';
/** StatusEffect 'hidden' — the Stealth-v2 stance ft_stealth_row_is_hidden() reads. */
const STATUS_HIDDEN = '47667406-1B72-9D60-310B-D8CF6BFE397D';
const STEALTH_ZONE_FT = 60;
const STEALTH_CLEAR_VIEW_MESSAGE = 'You cannot hide while a watcher can see you clearly — break line of sight, get into dim light or darkness, or leave their 60 ft awareness zone.';
const HUNKER_ALOFT_MESSAGE = 'You are flying — there is nothing here to hunker against.';
const HUNKER_NO_COVER_MESSAGE = 'There is no cover within reach to hunker behind.';
const CRAFT_SIZE_MESSAGE = 'That size is not available for this recipe.';
/** ft_crafting_wip_required_seconds(): crafting_time_type → seconds per unit (permanent / unknown = not a finite craft). */
const CRAFTING_SECONDS_PER_UNIT: Record<string, number> = { rounds: 6, seconds: 1, minutes: 60, hours: 3600, days: 86400, weeks: 604800, months: 2592000, years: 31104000 };
const CRAFTING_SIZE_MATERIAL_EXPONENT = 3;
const AMMO_ARROWS = new Set(['166EBD0E-7FC0-7351-5F82-C84F9B48565D', 'FBBD1092-449F-4322-9AF5-588825C2E3BD']);
const ARCANE_ARROW_VARIABLE = 'arcane_arrow';
const RANGE_SELF = 'D82855E1-4BB9-93EA-6374-B9904318BC1D';
const ARCANE_ARROW_SLOW_CASTING_TIMES = ['one_minute', 'ten_minutes', 'one_hour', 'eight_hours', 'twelve_hours', 'twenty_four_hours'];
const ARCANE_ARROW_UNBINDABLE_LAYERS = ['summon', 'user_choice_1_summon', 'shapeshift', 'user_choice_1_shapeshift', 'teleport', 'change_teleport_remaining',
  'spell_upcast_additional_projectiles_created', 'caster_moves_towards_target_in_ft', 'caster_moves_to_end_point_of_aoe_effect_after_cast'];
/** counterspell_lock_status_for_school(): school (circle / sphere) → its lockout status, and the school's catalog name. */
const COUNTERSPELL_LOCKS: Record<string, { status: string; name: string }> = {
  'DD1EFCA2-2A4C-AD3D-B814-A325B2D22D85': { status: '2A64D1E8-7C3B-4F92-B5A8-E19D64C72F35', name: 'Circle of Abjuration' },
  '54058786-9840-7A02-90F6-36151FA0A3F2': { status: '9E15B7A2-3D48-4C61-A927-F84B21D6E593', name: 'Circle of Charm' },
  '9931FE30-8ACF-40E2-8E69-EB78925364DF': { status: '6C82F394-1A5D-4E76-8B34-D29E75A1C648', name: 'Circle of Conjuration' },
  '5886822F-FA79-E5CA-C255-DCC7D375CB52': { status: '4F37A8D1-9B62-4A15-C783-28E91B5F4D76', name: 'Circle of Divination' },
  'A28D2D20-BCE9-1CE0-4371-7F42231E9C43': { status: '8D21C6F5-4E93-4B27-A164-73F8D2E9B5A4', name: 'Circle of Evocation' },
  'EC1F7FF4-9E4C-DAE0-633A-B0BE7F4A2788': { status: '3B96E4A7-2F51-4D38-9C82-46A1E7D3F925', name: 'Circle of Illusion' },
  'F5D2429C-6BA8-901F-2E23-30C780107C47': { status: '7A45D2B9-6C18-4F63-B591-82D4F6A3E1C7', name: 'Circle of Necromancy' },
  'CC4C52BD-A341-0CAC-6C0B-671C9CC1C2EC': { status: '5E63B1C4-8A27-4D95-A348-91C6E2F7D8B3', name: 'Circle of Transmutation' },
  '96E272A4-8DDE-443C-0FCF-3A24ECFFB7C2': { status: '1D87F5A3-6B42-4C19-8E75-34A9D1B6C2F8', name: 'Arcane Sphere of Magic' },
  '57F117E8-6659-4F2D-AED2-82D6005492B9': { status: '9C34A6E1-5D78-4B26-A493-67F2E8D5B1A9', name: 'Divine Sphere of Magic' },
  'B4D1AA5E-3B7B-4CE6-BA1E-DC43A5EEBF38': { status: '6F92C3D7-1E85-4A64-B237-58D1A9F4E6C3', name: 'Primal Sphere of Magic' },
};
const SUMMON_LAYERS = ['summon', 'user_choice_1_summon', 'user_choice_2_summon', 'user_choice_3_summon', 'user_choice_4_summon'];
const SHAPESHIFT_POOL_LAYERS = ['user_choice_1_shapeshift', 'user_choice_2_shapeshift', 'user_choice_3_shapeshift', 'user_choice_4_shapeshift'];
const ITEM_GRANT_MARKERS = ['at_will_spell', 'innate_spell_per_long_rest', 'charged_spell', 'charged_ability'];
const ROLL_CRAFTING = '54DC30E0-923C-4BBF-A8CC-570B0068E8A2';
/** OrbType::LIST — slug → display name (ft_orb_type_row). */
const ORB_NAMES: Record<string, string> = { lightning: 'Lightning Orb', cold: 'Cold Orb', necrotic: 'Necrotic Orb', thunder: 'Thunder Orb',
  fire: 'Fire Orb', force: 'Force Orb', poison: 'Poison Orb', acid: 'Acid Orb' };
/** htmlspecialchars(ENT_QUOTES | ENT_SUBSTITUTE) — the escaping PHP applies to author-controlled names in a warning. */
const escapeHtml = (value: string): string => value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#039;');

/** A held catalog/read name, else the requirement's own inline name. Identity-only references remain unknown. */
function itemName(id: string, read: AbilityPreconditionReaders, entry?: unknown): string | null {
  if (!UUID.test(id)) return null;
  const definition = read.resolveItem ? read.resolveItem(id) : null;
  const name = scalar(record(definition?.items)?.item_name ?? definition?.item_name);
  if (typeof name === 'string' && name !== '') return name;
  const fetched = read.name ? read.name(upper(id)) : null;
  if (typeof fetched === 'string' && fetched !== '') return fetched;
  const source = record(entry);
  const inline = itemField(record(source?.item_arr) ?? source, 'item_name');
  return typeof inline === 'string' && inline !== '' ? inline : null;
}
/** Preserve inline names when a layer reader unwraps its value to an id. */
function layerItemName(id: string, row: Row, key: string, read: AbilityPreconditionReaders): string | null {
  return itemName(id, read, read.entries?.(row, key).find(entry => upper(layerListEntryUuid(entry)) === id));
}
/** ft_combat_range_center(): the row's position bucket (x/y floor, z elevation), else the AOE-origin fallback; an absent
 * coordinate is 0 exactly as PHP reads it (positions are row columns, elided at 0), a non-numeric one is unknown. */
export function combatCenter(row: Row): { x: number; y: number; z: number } | null {
  const position = record(row.position) ?? record(row.pos_arr) ?? (('x_pos' in row || 'y_pos' in row || 'z_pos' in row) ? row : null);
  const read = (key: string, origin: string): number | null => {
    const raw = position ? scalar(position[key]) : undefined;
    if (raw !== undefined && raw !== null && numeric(raw)) return Number(raw);
    const fallback = position ? scalar(position[origin]) : undefined;
    return fallback !== undefined && fallback !== null && numeric(fallback) ? Number(fallback) : (raw === undefined || raw === null ? 0 : null);
  };
  const x = read('x_pos', 'status_origin_point_x'); const y = read('y_pos', 'status_origin_point_y'); const z = read('z_pos', 'status_origin_point_z');
  return x === null || y === null || z === null ? null : { x, y, z };
}
/** Frontend v3 range: target footprint rim horizontally, occupied span gap vertically. */
export function combatDistanceFt(a: { x: number; y: number; z: number }, b: { x: number; y: number; z: number }, sizeA: number | null, sizeB: number | null): number {
  const half = (size: number | null): number => size === null || !Number.isFinite(size) ? 0 : Math.max(0, size) / 2;
  const credit = (size: number | null): number => size === null || !Number.isFinite(size) ? 0 : Math.max(0, (size - 5) / 2);
  const dz = Math.max(0, Math.abs(b.z - a.z) - half(sizeA) - half(sizeB));
  const planar = Math.max(0, Math.sqrt((b.x - a.x) ** 2 + (b.y - a.y) ** 2) - half(sizeB));
  return Math.max(0, Math.sqrt(planar * planar + dz * dz) - credit(sizeA));
}
/** is_within_reach(): null when the page cannot measure it (a swarm's occupied width, a tagged reach, a non-numeric coordinate);
 * a sizeless caster is out of reach in PHP too. */
export function withinReach(caster: Row, target: Row, read: AbilityPreconditionReaders): boolean | null {
  if (read.has(caster, 'swarm_width_lanes')) return null; // ft_swarm_occupied_width_ft widens the caster
  const casterSize = combatSizeFt(caster, read);
  if (casterSize === null) return false; // PHP: an invalid caster size is out of reach
  const from = combatCenter(caster); const to = combatCenter(target);
  if (!from || !to) return null;
  const reach = read.value(caster, 'reach');
  if (reach !== null && reach !== undefined && reach !== '' && !numeric(reach)) return null; // a tagged reach the page does not resolve
  return combatDistanceFt(from, to, casterSize, combatSizeFt(target, read)) <= (numeric(reach) ? Number(reach) : 5);
}
/** discern_variable_tags() for a plain signed number; null when the value carries a tag the browser does not resolve. */
function plainNumber(raw: unknown): number | null {
  const text = String(raw ?? '').trim();
  if (!/^[+-]?\d+(?:\.\d+)?$/.test(text)) return null;
  const value = Number(text);
  return Number.isFinite(value) ? value : null;
}
/** The caster's carried inventory tree when the page holds it (an array, even empty); null when unheld. */
function casterInventory(caster: Row): Row[] | null {
  const raw = caster.inventory;
  if (!Array.isArray(raw)) return null;
  return raw.flatMap(entry => { const row = record(entry); return row ? [row] : []; });
}
/** Every row of a carried tree, containers recursed (inventory_in_container / container_contents). */
function walkInventory(rows: Row[]): Row[] {
  const out: Row[] = [];
  for (const row of rows) {
    out.push(row);
    const nested = row.inventory_in_container ?? row.container_contents;
    if (Array.isArray(nested)) out.push(...walkInventory(nested.flatMap(entry => { const child = record(entry); return child ? [child] : []; })));
  }
  return out;
}
/** Whether the page holds the caster's ability buckets at all (a compiled PC's computed.creature.abilities, or a creature's
 * own layer slots); absence of an ability is provable only then. */
function abilityBucketsHeld(caster: Row): boolean {
  return record(record(record(caster.computed)?.creature)?.abilities) !== null || record(caster.item_layer_types) !== null;
}
/** The caster-bucket entry for an ability id across the charge-bearing buckets (get_ability_charges_by_ability_id's walk). */
function findBucketEntry(caster: Row, abilityId: string, buckets: string[], read: Pick<AbilityPreconditionReaders, 'entries'> & { entries: NonNullable<AbilityPreconditionReaders['entries']> }): Row | null {
  for (const bucket of buckets) {
    for (const entry of abilityBucket(caster, bucket, read)) {
      if (entryId(entry) !== abilityId) continue;
      // A bare-UUID ref is a member too (PHP reads its id straight from the string); it carries no state of its own.
      return record(entry) ?? { item_id: entryId(entry) };
    }
  }
  return null;
}
/** ability_entry_name(): the flat item_name, else the page's catalog name; null when unknown (PHP would print a DB name). */
function entryName(entry: Row, id: string, read: AbilityPreconditionReaders): string | null {
  if (typeof entry.item_name === 'string' && entry.item_name !== '') return entry.item_name;
  const nested = record(entry.item_arr);
  const nestedName = scalar(record(nested?.items)?.item_name);
  if (typeof nestedName === 'string' && nestedName !== '') return nestedName;
  return itemName(id, read, entry);
}
/** The entry's own definition when it carries layers (item_arr / itself), else the page's catalog def; null when unheld. */
function entryDefinition(entry: Row, id: string, read: Pick<AbilityPreconditionReaders, 'resolveItem'>): Row | null {
  const nested = record(entry.item_arr);
  if (nested && record(nested.item_layer_types)) return nested;
  if (record(entry.item_layer_types)) return entry;
  return read.resolveItem ? read.resolveItem(id) : null;
}
/** resolve_inventory_layer_list_entry() → { uuid, template, qty } with PHP's string / rich-cell grammar (qty null when absent
 * or non-numeric, so a malformed requirement can be told from a missing one). */
export function inventoryListEntry(entry: unknown): { uuid: string; template: string | null; qty: number | null; rawQty: unknown } {
  const row = record(entry);
  if (row) {
    const uuid = layerListEntryUuid(entry);
    const template = row.template_id != null && row.template_id !== '' ? String(row.template_id) : null;
    const rawQty = 'qty' in row ? row.qty : undefined;
    const qty = rawQty != null && rawQty !== '' && numeric(rawQty) ? Number(rawQty) : null;
    return { uuid, template, qty, rawQty };
  }
  const s = String(entry ?? '').trim();
  if (s === '' || !s.includes('|')) return { uuid: s, template: null, qty: null, rawQty: undefined };
  const [a, b] = [s.slice(0, s.indexOf('|')), s.slice(s.indexOf('|') + 1)];
  if (numeric(b)) return { uuid: a, template: null, qty: Number(b), rawQty: b };
  return { uuid: a, template: UUID.test(b) ? b : null, qty: null, rawQty: undefined };
}
/** ability_entry_charges() / ability_entry_max_charges() for a caster-bucket row (charges cluster, legacy flat, riding def). */
export function entryCharges(entry: Row, id: string, read: Pick<AbilityPreconditionReaders, 'value' | 'resolveItem'>): { current: unknown; max: unknown; currentHeld: boolean } | null {
  const charges = record(entry.charges);
  const current = charges && 'current' in charges ? charges.current : 'ability_charges' in entry ? entry.ability_charges : undefined;
  const max = charges && 'max' in charges ? charges.max : 'max_ability_charges' in entry ? entry.max_ability_charges : undefined;
  if (current !== undefined && max !== undefined) return { current, max, currentHeld: true };
  // PHP falls back to the DEFINITION's ability_charges (the riding def, else a catalog resolve); the page can only do the same
  // when it holds that def — otherwise the state is unknown.
  const definition = entryDefinition(entry, id, read);
  if (!definition) return null;
  const fallback = read.value(definition, 'ability_charges');
  return { current: current !== undefined ? current : fallback, max: max !== undefined ? max : fallback, currentHeld: current !== undefined };
}

/** ft_fabricate_rows(): the recipe's output rows (uuid / template / whole qty ≥ 1); a row without a uuid is skipped. */
export function fabricateRows(entries: unknown[]): Array<{ uuid: string; template: string; qty: number }> {
  const rows: Array<{ uuid: string; template: string; qty: number }> = [];
  for (const entry of entries) {
    if (entry === null || entry === '' || (Array.isArray(entry) && entry.length === 0)) continue;
    const resolved = inventoryListEntry(entry);
    const uuid = resolved.uuid.trim();
    if (!UUID.test(uuid)) continue;
    // (int) round((float) qty) floored at 1; a null / non-numeric qty is one unit.
    const qty = Math.max(1, resolved.qty !== null ? Math.round(resolved.qty) : 1);
    let template = (resolved.template ?? '').trim();
    // A pipe-packed template inside a STRUCTURED row (item_layer_effect = "uuid|template").
    const row = record(entry);
    if (template === '' && row) {
      const effect = String(row.item_layer_effect ?? '');
      if (effect.includes('|')) {
        const packed = effect.slice(effect.indexOf('|') + 1).trim();
        if (UUID.test(packed)) template = packed;
      }
    }
    rows.push({ uuid, template: UUID.test(template) ? template : '', qty });
  }
  return rows;
}
/** One requirement row of a craft manifest (ft_crafting_wip_build_manifest / ft_crafting_scale_manifest). */
export interface CraftManifestRow { id: string; template: string | null; qty: number }
/** One output row of a craft plan (ft_crafting_build_plan's `outputs`): the snapshot the WIP tracker records. */
export interface CraftOutputRow { item_uuid: string; template_id: string | null; qty: number; size_id: string | null; weight_override: number | null }
/** ft_crafting_build_plan()'s result as the craft start persists it (the tracker payload v2: required seconds, manifest, outputs, fv, sz). */
export interface CraftPlan { requiredSeconds: number; manifest: CraftManifestRow[]; outputs: CraftOutputRow[]; sizeId: string | null; formulaVersion: number }
/** FT_CRAFTING_SIZE_FORMULA_VERSION / _LABOR_EXPONENT / _MIN_SECONDS (crafting_size_scaling.php). */
const CRAFTING_SIZE_FORMULA_VERSION = 2;
const CRAFTING_SIZE_LABOR_EXPONENT = 2;
const CRAFTING_SIZE_MIN_SECONDS = 60;
/** ft_crafting_build_plan() (crafting_size_scaling.php) in full. A refusal names which of crafting_roll's start messages applies:
 * 'time' (no finite crafting time), 'manifest' (a malformed reagent row), 'plan' (a requested size the recipe cannot take, a negative
 * scaled weight, no output). null when the page cannot decide (the output def unheld). */
export function craftingPlan(ability: Row, requestedSizeRaw: unknown, read: AbilityPreconditionReaders & { entries: NonNullable<AbilityPreconditionReaders['entries']> })
  : CraftPlan | { refuse: 'time' | 'manifest' | 'plan' } | null {
  // ft_crafting_wip_required_seconds(): both time layers held, a whole positive count, a finite unit.
  if (!read.has(ability, 'crafting_time_int') || !read.has(ability, 'crafting_time_type')) return { refuse: 'time' };
  const durationInt = read.value(ability, 'crafting_time_int');
  if (!numeric(durationInt) || phpIntval(durationInt) < 1) return { refuse: 'time' };
  const perUnit = CRAFTING_SECONDS_PER_UNIT[String(read.value(ability, 'crafting_time_type') ?? '')] ?? 0;
  if (!(perUnit > 0)) return { refuse: 'time' };
  const requiredSeconds = phpIntval(durationInt) * perUnit;
  // ft_crafting_wip_build_manifest(): aggregate by item|template; a bad uuid or a qty below 1 voids the plan.
  const aggregated = new Map<string, CraftManifestRow>();
  for (const entry of read.entries(ability, 'requires_reagent')) {
    if (entry === null || entry === '' || (Array.isArray(entry) && entry.length === 0)) continue;
    const resolved = inventoryListEntry(entry);
    const id = resolved.uuid.trim();
    const template = (resolved.template ?? '').trim();
    // (int) ($resolved['qty'] ?? 1): an absent / null qty is one unit; a non-numeric one casts to 0.
    const qty = resolved.rawQty === undefined || resolved.rawQty === null ? 1 : phpIntval(resolved.rawQty);
    if (!UUID.test(id) || qty < 1) return { refuse: 'manifest' };
    const key = `${id.toLowerCase()}|${template.toLowerCase()}`;
    const slot = aggregated.get(key) ?? { id: upper(id), template: UUID.test(template) ? upper(template) : null, qty: 0 };
    slot.qty += qty;
    aggregated.set(key, slot);
  }
  // ft_crafting_requested_size_from_options(): the raw secondary slot, trimmed; a non-string is no request.
  const requested = typeof requestedSizeRaw === 'string' ? requestedSizeRaw.trim() : '';
  const outputs = fabricateRows(read.entries(ability, 'fabricate'));
  let fMat = 1;
  let fTime = 1;
  let scaled = false;
  let scaledWeight: number | null = null;
  if (requested !== '') {
    if (!UUID.test(requested)) return { refuse: 'plan' };
    // ft_crafting_recipe_is_size_scalable(): the flag, then ONE output identity whose def resolves and carries a size layer.
    if (phpIntval(read.value(ability, 'size_scalable') ?? 0) === 0) return { refuse: 'plan' };
    if (outputs.length === 0) return { refuse: 'plan' };
    let identity: string | null = null;
    for (const row of outputs) {
      const key = `${row.uuid}|${row.template}`.toLowerCase();
      if (identity !== null && identity !== key) return { refuse: 'plan' };
      identity = key;
    }
    if (!read.resolveItem) return null;
    const output = read.resolveItem(outputs[0]!.uuid);
    if (!output) return null; // PHP's query_full_item_arr may still find it: the page cannot prove the output is missing
    const defaultRaw = read.value(output, 'size');
    const defaultSize = typeof defaultRaw === 'string' && UUID.test(defaultRaw) && SIZE_WIDTH_FT[upper(defaultRaw)] !== undefined ? upper(defaultRaw) : null;
    if (defaultSize === null) return { refuse: 'plan' };
    // ft_crafting_size_factors(): both ids must be ladder rungs with a positive base_size.
    const requestedBase = SIZE_WIDTH_FT[upper(requested)];
    const defaultBase = SIZE_WIDTH_FT[defaultSize]!;
    if (requestedBase === undefined || !(requestedBase > 0) || !(defaultBase > 0)) return { refuse: 'plan' };
    scaled = upper(requested) !== defaultSize;
    if (scaled) {
      fMat = (requestedBase / defaultBase) ** CRAFTING_SIZE_MATERIAL_EXPONENT;
      fTime = (requestedBase / defaultBase) ** CRAFTING_SIZE_LABOR_EXPONENT;
      // A scaled output whose authored weight goes negative (or unencodable) is a malformed recipe, not a craft. Every row shares the
      // one output identity, so the first row's def is every row's def.
      const weight = read.value(output, 'weight');
      if (numeric(weight)) {
        const w = Number(weight) * fMat;
        if (!Number.isFinite(w) || w < 0) return { refuse: 'plan' };
        // DECIMAL(20,2): round to cents; a positive mass never rounds to zero.
        scaledWeight = w > 0 ? Math.max(0.01, Math.round(w * 100) / 100) : w;
      }
    }
  }
  // A craft that produces NOTHING is refused before any material is consumed.
  if (outputs.length === 0) return { refuse: 'plan' };
  // ft_crafting_scale_manifest(): ceil per row (epsilon-guarded), floor 1; untouched at f = 1.
  const manifest = [...aggregated.values()].map(row => ({ ...row, qty: fMat === 1 ? row.qty : Math.max(1, Math.ceil(row.qty * fMat - 1e-9)) }));
  return {
    // ft_crafting_scale_seconds(): whole seconds, a 60 s floor, untouched when f_time = 1.
    requiredSeconds: fTime === 1 ? requiredSeconds : Math.max(CRAFTING_SIZE_MIN_SECONDS, Math.ceil(requiredSeconds * fTime - 1e-9)),
    manifest,
    outputs: outputs.map(row => ({ item_uuid: row.uuid, template_id: row.template !== '' ? row.template : null, qty: row.qty,
      size_id: scaled ? upper(requested) : null, weight_override: scaled ? scaledWeight : null })),
    sizeId: scaled ? upper(requested) : null,
    formulaVersion: CRAFTING_SIZE_FORMULA_VERSION,
  };
}
/** The plan as the reagent gate consumes it: the manifest; 'refuse' when PHP's plan is null; null when the page cannot decide. */
function craftingBuildPlan(ability: Row, requestedSizeRaw: unknown, read: AbilityPreconditionReaders & { entries: NonNullable<AbilityPreconditionReaders['entries']> })
  : { manifest: CraftManifestRow[] } | 'refuse' | null {
  const plan = craftingPlan(ability, requestedSizeRaw, read);
  if (plan === null) return null;
  return 'refuse' in plan ? 'refuse' : { manifest: plan.manifest };
}

/** status_instance_has_campaign_pause() */
function campaignPaused(status: Row): boolean {
  return ['year', 'month', 'day', 'hour', 'minute', 'second'].some(part => {
    const paused = scalar(status[`status_paused_campaign_${part}`]);
    return paused != null && paused !== '' && Number(paused) !== 0;
  });
}
/** Live, public status definitions (the rows PHP's action-economy and discount readers consider). `complete` is false
 * when any live status's definition is not held locally: it could carry a discount or AP override, so every
 * refusal that a discount could lift stands down. */
function liveStatusDefinitions(caster: Row, read: AbilityPreconditionReaders): { definitions: Row[]; complete: boolean } {
  let complete = true;
  const definitions = read.statuses(caster).flatMap(status => {
    if (campaignPaused(status) || !phpEmpty(scalar(status.status_private))) return [];
    const id = itemsCol<string>(status, 'item_id');
    const inline = record(status.status_arr);
    const fetched = !inline && id && read.resolveItem ? read.resolveItem(upper(id)) : null;
    const definition = inline ?? (heldLayerlessDefinition(fetched) ? fetched : null);
    if (!definition) complete = false;
    return definition ? [definition] : [];
  });
  return { definitions, complete };
}

/** PHP's refusal message when the cached rows prove the cast would be refused; null otherwise (allowed OR undecidable). */
export function abilityPreconditionRefusal(input: AbilityPreconditionInput, read: AbilityPreconditionReaders): string | null {
  const verdict = judgeAbilityPreconditions(input, read);
  return typeof verdict === 'string' ? verdict : null;
}

/** R-025 S5 — the three-way verdict the dispatch acts on (see the header). */
export function abilityGateVerdict(input: AbilityPreconditionInput, read: AbilityPreconditionReaders): AbilityGateVerdict {
  const verdict = judgeAbilityPreconditions(input, read);
  if (typeof verdict === 'string') return { kind: 'refuse', message: verdict };
  return verdict ? { kind: 'unknown', site: verdict.unknown } : { kind: 'allow' };
}

/** janus_internal_dispatch_rules: only the retained PHP subset. This is called by an engine frame, never the public cast gate. */
export function timedFinishGateVerdict(input: AbilityPreconditionInput, read: AbilityPreconditionReaders, timedStatusRowId: string): AbilityGateVerdict {
  if (!UUID.test(timedStatusRowId)) return { kind: 'refuse', message: 'That spell has not finished casting yet.' };
  const verdict = judgeAbilityPreconditions(input, read, true);
  return typeof verdict === 'string' ? { kind: 'refuse', message: verdict } : verdict ? { kind: 'unknown', site: verdict.unknown } : { kind: 'allow' };
}

/** PHP's refusal message; an AbilityGateUnknown when a rule PHP runs could not be judged; null when every rule passed. An
 * explicit stand-down returns at once (PHP may refuse there, so no later rule may speak for it); a rule that is merely skipped
 * for missing data marks the verdict pending and lets later PROVEN refusals still speak — a later refusal is one PHP makes too. */
function judgeAbilityPreconditions(input: AbilityPreconditionInput, read: AbilityPreconditionReaders, internal = false): string | AbilityGateUnknown | null {
  let pending: string | null = null;
  const standDown = (site: string): AbilityGateUnknown => ({ unknown: site });
  const undecided = (site: string): void => { if (pending === null) pending = site; };
  const allowed = (): AbilityGateUnknown | null => pending === null ? null : standDown(pending);
  // An ALLOW must never rest on a rule skipped for want of a reader: without the full reader set every rule guarded by one could
  // pass silently, so a partial reader set can refuse (what it proves) but never allow (Codex review).
  if (!(read.entries && read.inventoryField && read.resolveItem && read.resolveInventory && read.creatureSizeId && read.itemSizeId)) undecided('readers');
  const { ability } = input;
  const summonControl = [DISMISS_SUMMON, RECALL_SUMMON].includes(upper(itemField(ability, 'item_id')));
  const caster = summonControl ? input.actionCaster : input.caster;
  if (!caster) return standDown('summon_control:controller-not-held');
  if (summonControl) {
    const controllerId = upper(ecLean(input.caster, 'controller_encounter_creature_id'));
    if (!controllerId || controllerId === ecId(input.caster) || ecId(caster) !== controllerId
        || upper(ecLean(caster, 'encounter_id')) !== upper(ecLean(input.caster, 'encounter_id')))
      return standDown('summon_control:controller-not-held');
  }
  const v1 = input.variable1;
  const hasV1 = v1 !== undefined && v1 !== null;
  const ec = record(caster.encounter_creatures) ?? {};
  // Only an encounter row that is provably the caster's own counts; anything else leaves combat state unknown.
  const encounterId = input.encounter ? upper(encounterField(input.encounter, 'encounter_id')) : '';
  const encounter = encounterId !== '' && encounterId === upper(scalar(ec.encounter_id)) ? input.encounter : null;
  const statusRead = liveStatusDefinitions(caster, read);
  const itemType = String(itemsCol(ability, 'item_type') ?? '');
  const spellLike = SPELL_LIKE.includes(itemType);
  // The compiled EC row is lean-elided — an absent scalar IS 0 (movement_pool.php; PHP reads `?? 0`). That holds only
  // for a real compiled row; a caster without one leaves every EC scalar unknown.
  const ecCompiled = typeof scalar(ec.encounter_creature_id) === 'string' && String(scalar(ec.encounter_creature_id)) !== '';
  const ecScalar = (key: string): unknown => {
    const value = scalar(ec[key]);
    return value === undefined && ecCompiled ? 0 : value;
  };
  const manaRaw = ecScalar('mana');
  const manaKnown = numeric(manaRaw);
  const manaProtected = manaReductionDelta(caster, -1, read) === 0;
  const chargesProtected = chargeReductionDelta(caster, -1, read) === 0;

  // COMPOSITE ROWS (R-025 S5): janus judges an attack on the RESOLVED weapon row with the ability nested under attack_ability_arr,
  // and the row's identity is its item_id_used (the weapon's inventory id). The rules PHP fans out over both layers (equipment
  // set, charge rules, orbs, mana, luck — janus_item_layers_for_ability_charge_rules, rider first) read `layers`; the weapon-type
  // rule reads the rider; every other rule reads the outer row, exactly as use_ability_pre_conditions() does. A bare attack
  // ability (the page did not resolve its weapon) or an engine-marked row the page did not build is not judged.
  const rider = record(ability.attack_ability_arr);
  if (itemType === '') return standDown('item-type-unread');
  if (!rider && ability.item_id_used !== undefined) return standDown('engine-marked-row');
  if (!rider && (itemType === 'attack_ability' || itemType === 'opportunity_attack_ability')) return standDown('attack-weapon-unresolved');
  if (rider && itemType !== 'weapon' && itemType !== 'natural_weapon') return standDown('composite-shape');
  const layers: Row[] = rider ? [rider, ability] : [ability];
  const layerId = (layer: Row): string => upper(scalar(layer.item_id_used) ?? itemField(layer, 'item_id'));
  const abilityId = layerId(ability);
  if (read.entries && layers.some(layer => read.has(layer, 'ability_charges') || read.has(layer, 'consumes_ability_charges'))) {
    const vehicle = resolvePilotedObjectInEncounter(caster, input.encounter ?? null);
    if (vehicle && abilityBucket(caster, itemType, { entries: read.entries }).some(entry => entryId(entry) === abilityId
        && upper(itemField(record(entry), 'source')) === vehicle))
      return standDown('piloted-abilities:vehicle-charge-pool');
  }
  const v2 = input.variable2;
  const hasV2 = v2 !== undefined && v2 !== null;
  const cachedInventoryRow = (id: unknown): Row | null => (read.resolveInventory && typeof id === 'string' && UUID.test(id)) ? read.resolveInventory(id) : null;

  // Absorbed gear: inspect the resolved item, source inventory and consumed item before every mode.
  if (read.inventoryField) {
    const absorbed = [record(ability.inventory) ? ability : null, cachedInventoryRow(v2), cachedInventoryRow(input.consumeInventoryId)];
    for (const row of absorbed) {
      if (row && phpIntval(read.inventoryField(row, 'inventory_private')) === 2) return 'That item is merged into your current form and cannot be used until you revert.';
    }
    // A sheet grant's source is provenance, including native carriers absent from
    // the catalog. The grant/innate gates below read its actual ability and pool;
    // isSpellGrantCast checks that this source grants this spell before dispatch.
    const grantSource = typeof v2 === 'string' && UUID.test(v2) && isSpellGrantMode(v1)
      && read.entries?.(caster, v1).some(entry => upper(read.value(record(entry), 'source')) === upper(v2));
    if (typeof v2 === 'string' && UUID.test(v2) && !absorbed[1] && !grantSource && !(read.resolveItem?.(v2))) undecided('side-channel-row');
    if (typeof input.consumeInventoryId === 'string' && UUID.test(input.consumeInventoryId) && !absorbed[2]) undecided('consume-row');
  } else {
    undecided('readers');
  }

  // FORGED DELAYED-CAST FINISH (L101–112, 2026-09-16): the finish markers are engine-only — janus threads timed_status_row_id
  // for the timed-expiration path alone, and no browser request can carry it, so a posted finish is always refused.
  if (!internal && hasV1 && ['finish_casting', 'finish_casting_ritual_spell'].includes(String(v1))) return 'That spell has not finished casting yet.';

  // ARCANE ARROW (L101–113, janus/core/arcane_fletcher.php ft_arcane_arrow_context): the bound-scroll shot's own refusals,
  // up to the scroll's spell. The spell-shape rules (self-only / slow / aura / unbindable) re-read the catalog spell — S3.
  if (hasV1 && String(v1) === ARCANE_ARROW_VARIABLE && read.entries && read.resolveItem && read.inventoryField) {
    // Absence of a provider is provable only when the page holds the passive bucket itself (a compiled PC's
    // computed.creature.abilities, or a creature's own layer slot); an unheld bucket is not an empty one.
    const passiveBucketHeld = record(record(record(caster.computed)?.creature)?.abilities) !== null
      || (record(caster.item_layer_types) !== null && 'passive_ability' in (record(caster.item_layer_types) as Row));
    if (!passiveBucketHeld) return standDown('arcane-arrow-provider-bucket');
    let providerUnknown = false;
    let provider = false;
    for (const entry of abilityBucket(caster, 'passive_ability', { entries: read.entries })) {
      // PHP wraps a bare-UUID ref as an active entry; only a stamped row can be inactive.
      if (typeof entry !== 'string' && !passiveEntryActive(entry)) continue;
      const row = record(entry);
      const nested = record(row?.item_arr);
      const definition = nested && record(nested.item_layer_types) ? nested : read.resolveItem(entry);
      if (!definition) { providerUnknown = true; continue; }
      if (!phpEmpty(read.value(definition, 'arcane_fletcher')) && UUID.test(entryId(entry))) { provider = true; break; }
    }
    if (!provider && providerUnknown) return standDown('arcane-arrow-provider-def');
    if (!provider) return 'You do not know how to bind a spell scroll to an arrow.';
    if (itemType !== 'weapon' || !AMMO_ARROWS.has(upper(read.value(ability, 'uses_ammo_classification')))) return 'Arcane Arrow needs a bow that fires arrows.';
    if (!(typeof v2 === 'string' && UUID.test(v2))) return 'Choose a spell scroll to bind to the arrow.';
    const carried = casterInventory(caster);
    if (carried === null) return standDown('arcane-arrow-inventory');
    const { inventoryField } = read;
    const scroll = walkInventory(carried).find(row => phpIntval(inventoryField(row, 'inventory_deleted')) === 0 && upper(inventoryField(row, 'inventory_id')) === upper(v2)) ?? null;
    if (!scroll) return 'You are not carrying that scroll.';
    if (String(itemField(scroll, 'item_type') ?? '') !== 'scroll') return 'Only a spell scroll can be bound to an arrow.';
    if (!phpEmpty(inventoryField(scroll, 'inventory_unidentified'))) return 'You must identify that scroll before binding it to an arrow.';
    const charges = read.value(scroll, 'current_item_charges');
    if (!chargesProtected && charges !== null && charges !== undefined && charges !== '' && phpIntval(charges) <= 0) return 'That scroll has already been spent.';
    const enchantment = record(record(scroll.computed)?.enchantment);
    // "No spell" is provable only from a held enchantment cluster carrying its abilities slot (a thinned page row has
    // neither). An EMPTY PHP cluster serializes as [] — an array, not a record — and is still a held, empty slot.
    if (enchantment && enchantment.abilities !== undefined && enchantment.abilities !== null) {
      const charged = record(enchantment.abilities)?.charged_spell;
      const spellId = Array.isArray(charged) ? charged.map(entry => entryId(entry)).find(id => UUID.test(id)) : undefined;
      const templateSpell = Array.isArray(scroll.enchantment_template_enchantments)
        ? scroll.enchantment_template_enchantments.map(e => record(e)).find(e => e && e.enchantment_type === 'charged_spell' && UUID.test(String(e.enchantment_item_id ?? '')))
        : undefined;
      if (!spellId && !templateSpell) return 'That scroll holds no spell an arrow can carry.';
      // ft_arcane_arrow_spell_refusal() re-reads the catalog spell; the page judges it only when it holds that def.
      const boundId = spellId ?? String(templateSpell?.enchantment_item_id ?? '');
      const spell = read.resolveItem(boundId);
      if (!spell || !record(spell.item_layer_types)) return standDown('arcane-arrow-spell-def');
      const boundType = itemField(spell, 'item_type');
      if (typeof boundType !== 'string' || boundType === '') return standDown('arcane-arrow-spell-type');
      if (boundType !== 'spell') return 'That scroll holds no spell an arrow can carry.';
      const hasAoe = read.value(spell, 'aoe_type') !== null && read.value(spell, 'aoe_type') !== undefined;
      if (upper(read.value(spell, 'range')) === RANGE_SELF && !hasAoe) return 'A spell that only affects its caster cannot be bound to an arrow.';
      if (ARCANE_ARROW_SLOW_CASTING_TIMES.includes(String(read.value(spell, 'spell_casting_time') ?? ''))) return 'A spell with a long casting time cannot be bound to an arrow.';
      const isAura = !phpEmpty(read.value(spell, 'aoe_follows_target'))
        || (hasAoe && read.value(spell, 'aoe_distance') != null && read.value(spell, 'persistent_aoe') != null && read.value(spell, 'aoe_follows_target') != null);
      if (isAura) return 'An aura spell cannot be bound to an arrow.';
      if (ARCANE_ARROW_UNBINDABLE_LAYERS.some(layer => read.has(spell, layer))) return 'That spell cannot be bound to an arrow.';
      return standDown('arcane-arrow-bow-attack'); // the arrow context is sound; PHP goes on to judge the bow attack itself
    }
    return standDown('arcane-arrow-enchantment-cluster'); // "holds a spell" is unknown without the enchantment cluster
  }
  if (hasV1 && String(v1) === ARCANE_ARROW_VARIABLE) return standDown('arcane-arrow-readers');

  // HURLED OBJECT (2026-10-08, hurls_unattended_object — the Psion Telekinetic hurl mentas): the cast needs a picked, still-eligible
  // unattended floor object (object-handling-layers.ts hurlObjectRowRefusal through the page binder). Browser-only rule (R-188 persists).
  if (read.value(ability, HURLS_UNATTENDED_OBJECT) != null) {
    if (!read.hurlObjectRefusal) undecided('hurl-object');
    else { const hurl = read.hurlObjectRefusal(caster, ability); if (hurl) return hurl; }
  }

  let throwDrawAp = 0;
  if (hasV1 && String(v1) === THROW_OBJECT_VARIABLE) {
    if (read.inventoryField && read.resolveInventory && read.creatureSizeId && read.itemSizeId) {
      const row = input.variable2 != null && input.variable2 !== '' ? read.resolveInventory(String(input.variable2)) : null;
      const { inventoryField, creatureSizeId, itemSizeId, entries, resolveItem } = read;
      const throwMaxSizeId = entries && resolveItem
        ? (creature: Row) => throwObjectMaxSizeId(creature, { value: read.value, entries, definition: resolveItem }) : undefined;
      const verdict = throwObjectVerdict({ caster, casterId: ecId(caster), row, encounterId: String(scalar(ec.encounter_id) ?? ''), encounter },
        { value: read.value, inventoryField, itemField: (r, key) => itemField(r, key), creatureSizeId, itemSizeId, throwMaxSizeId,
          telekineticReach: read.telekineticReach, resolveInventory: read.resolveInventory });
      if (verdict.refusal) return verdict.refusal;
      if (upper(itemField(ability, 'item_id')) !== THROW_OBJECT_ABILITY_ID) undecided('variable-mode:throw_inventory');
      else {
        const rangeRefusal = throwObjectRangeRefusal(verdict);
        if (rangeRefusal) return rangeRefusal;
        if (verdict.mode === 'draw') throwDrawAp = THROW_OBJECT_DRAW_AP;
      }
    } else undecided('throw-object-readers');
  }

  // Equipment-set cast admission shares the condition compiler's catalog requirement reader.
  for (const layer of layers) {
    if (!read.has(layer, 'requires_equipped_item')) continue;
    if (!read.entries || !read.inventoryField) { undecided('equipment-set-readers'); break; }
    if (!hasLiveEquipmentContext(caster)) {
      undecided('equipment-set-inventory'); break;
    }
    const missing = unmetEquippedItemRequirements(caster, layer, { entries: read.entries, inventoryField: read.inventoryField });
    if (missing.length) {
      const names = missing.filter(id => UUID.test(id)).map(id => layerItemName(id, layer, 'requires_equipped_item', read));
      if (names.every((name): name is string => name !== null)) {
        return `This power is part of an equipment set — you must have ALL of its pieces equipped to use it. Missing: ${names.map(escapeHtml).join(', ')}.`;
      }
      return standDown('equipment-set-name');
    }
  }

  // Mode sentinels skip the generic gates from here on (damage ticks, book reads, arming a counterspell…).
  if (hasV1 && ACTION_SENTINELS.includes(String(v1))) return allowed();

  // MV1 ask 2 (owner-authorized MV4 edit, 2026-09-28; plans/janus-m4b-teleport-m3d-burrow-plan-2026-09-24.md §3): a caster inside
  // solid (Burrowed) casts nothing — moving (emerging) is not a cast. A browser gate, never a no_actions layer (that would refuse the
  // burrow lane itself). The engine sentinels above (the Burrowed crush tick is a damage_only dispatch) still pass. PHP twin:
  // use_ability_pre_conditions() (the server subset), so a legacy dispatch refuses the same way.
  if (read.statuses(caster).some(status => upper(scalar(status.status_item_id) ?? scalar(record(record(status.status_arr)?.items)?.item_id)) === STATUS_BURROWED))
    return 'You are burrowed. Move to emerge first.';

  // SHAPESHIFT POOL (L169–200): a posted form must be in the authored pool; a pool-only shapeshift needs a form at all.
  if (SHAPESHIFT_POOL_LAYERS.some(layer => read.has(ability, layer)) && read.entries) {
    const formIsUuid = typeof v1 === 'string' && UUID.test(v1);
    if (formIsUuid) {
      const pool = ['shapeshift', ...SHAPESHIFT_POOL_LAYERS].flatMap(layer => read.entries!(ability, layer).map(entry => upper(layerListEntryUuid(entry))));
      if (!pool.includes(upper(v1))) return 'This form is not offered by this ability or spell!';
    } else if (!read.has(ability, 'shapeshift')) {
      return 'Select a form to transform into before casting this.';
    }
  }

  // ITEM-SOURCED GRANT (L209–274) + the INNATE POOL (L276–313): an at-will / innate / charged cast must still be granted
  // by a held item (present in a caster bucket, or a branch of a held ability_choice parent); an innate cast needs a use
  // left in its per-long-rest pool. Absence is provable only when the page holds the buckets; the innate count only when
  // the compiled entry carries its charges cluster (a pool with no row yet is FULL — never assumed empty).
  if (hasV1 && typeof v1 === 'string' && ITEM_GRANT_MARKERS.includes(v1) && abilityId !== '' && read.entries) {
    if (!abilityBucketsHeld(caster)) return standDown('item-grant-buckets');
    const entriesRead = { entries: read.entries };
    const scanBuckets = [...new Set([...ABILITY_TYPES, ...ITEM_GRANT_MARKERS])];
    let present = findBucketEntry(caster, abilityId, scanBuckets, entriesRead) !== null;
    let parentId = '';
    if (!present) {
      // ABILITY_CHOICE BRANCH: authorized through a held PARENT whose ability_choice list names this child — scanned only in
      // the bucket the client claimed (charged_ability: every bucket).
      const branchBuckets = v1 === 'charged_ability' ? scanBuckets : [v1];
      const seen = new Set<string>();
      let unknown = false;
      outer: for (const bucket of branchBuckets) {
        for (const entry of abilityBucket(caster, bucket, entriesRead)) {
          const candidate = entryId(entry);
          if (!UUID.test(candidate) || seen.has(candidate) || candidate === abilityId) continue;
          seen.add(candidate);
          const row = record(entry) ?? { item_id: candidate };
          const definition = entryDefinition(row, candidate, read);
          if (!definition) { unknown = true; continue; }
          const children = read.entries(definition, 'ability_choice').map(child => {
            const c = record(child);
            if (!c) return upper(child);
            return upper(scalar(c.ability_id) ?? (typeof c.item_id === 'string' ? c.item_id : undefined) ?? c[0]);
          });
          if (children.includes(abilityId)) { present = true; parentId = candidate; break outer; }
        }
      }
      if (!present && unknown) return standDown('item-grant-branch-def');
    }
    if (!present) return 'You can no longer use this — the item that granted it is no longer equipped.';
    if (v1 === 'innate_spell_per_long_rest') {
      const poolId = parentId !== '' ? parentId : abilityId;
      const poolEntry = findBucketEntry(caster, poolId, ['innate_spell_per_long_rest'], entriesRead);
      // PHP's charge holder prefers the caster's OWN inventory_id (an item acting as caster) over the EC id; the page's charge
      // evidence is the creature's, so an inventory-held pool is not judged here.
      const inventoryHolder = read.inventoryField ? read.inventoryField(caster, 'inventory_id') : null;
      if (poolEntry && inventoryHolder !== null && inventoryHolder !== undefined && inventoryHolder !== '') return standDown('innate-pool-holder');
      const holder = scalar(ec.encounter_creature_id);
      if (poolEntry && typeof holder === 'string' && holder !== '') {
        // The pool's state: the entry's compiled charges cluster, else the pre-baked castable payload
        // (computed.creature.castable.innate_spell_per_long_rest[].charges — build_castable_spells_data reads the same
        // charges row the gate reads). Neither on the page = unknown; PHP may refuse right here, so the whole gate stands
        // down (the owner's Q2 fetch-on-miss fills the castable payload before this gate runs — ability-preconditions-browser.ts).
        let current: unknown = record(poolEntry.charges)?.current;
        if (current === undefined) {
          const castable = record(record(record(caster.computed)?.creature)?.castable);
          const innateList = castable?.innate_spell_per_long_rest;
          const baked = Array.isArray(innateList) ? innateList.map(row => record(row)).find(row => row && upper(row.item_id) === poolId) : null;
          current = record(baked?.charges)?.current;
        }
        if (current === undefined || !numeric(current)) return standDown('innate-pool-charges');
        if (!chargesProtected && phpIntval(current) < 1) return 'No uses left — this recharges on a long rest.';
      }
    }
  }

  // [x]-mana investment (L288–311).
  const dice = read.value(ability, 'dice');
  const manaChange = read.value(ability, 'change_caster_mana');
  if (!internal && ((dice === '[x]' && itemType !== 'menta') || (manaChange != null && String(manaChange).includes('[x]')))) {
    if (!hasV1 || !numeric(v1) || phpIntval(v1) < 1) return 'Enter how much mana to invest (number of damage dice) for this ability.';
    if (manaKnown && phpIntval(v1) > manaInvestmentBudget(caster, Number(manaRaw), read)) return manaProtected
      ? 'You cannot invest more mana than the higher of your current mana and spirit.'
      : 'You cannot invest more mana than you currently have.';
    if (!manaKnown) undecided('x-mana-unknown');
  }

  // check_caster_aps_and_in_combat (L314).
  if (!internal && !SKILL_TYPES.includes(itemType)) {
    const combatKnown = encounter !== null;
    // A caster seated in an encounter whose row the page does not hold (or not provably its own) leaves combat state unknown.
    if (!combatKnown && upper(scalar(ec.encounter_id)) !== '') undecided('combat-state');
    const inCombat = combatKnown && (summonControl ? read.inCombat(encounter!, caster) : Number(encounterField(encounter, 'in_combat') ?? 0) === 1);
    const permissions = record(caster.permissions);
    const gmKnown = typeof permissions?.campaign === 'boolean';
    const isGM = permissions?.campaign === true;
    const isOpportunityAttack = itemType === 'opportunity_attack_ability' || String(itemField(rider, 'item_type') ?? '') === 'opportunity_attack_ability';
    const castingTime = spellLike ? String(read.value(ability, 'spell_casting_time') ?? '3ap') : '';
    const controlCost = summonControl ? abilityActionCost(itemType, read.value(ability, 'spell_casting_time')) : null;
    if (summonControl && controlCost === null) return standDown('summon_control:casting-time');
    if (inCombat && !gmKnown && !summonControl) undecided('gm-permission');
    if (inCombat && (summonControl || (gmKnown && !isGM))) {
      const myTurn = upper(encounterField(encounter!, 'current_encounter_creature_id_turn')) === ecId(caster);
      const offTurn = summonControl ? controlCost === 'r' || (itemType === 'free_ability' && controlCost === 0)
        : OUT_OF_TURN_TYPES.includes(itemType) || (spellLike && castingTime === 'reaction') || isOpportunityAttack;
      if (!myTurn && !offTurn) {
        return 'While in combat, you can only use abilities that use reactions or triggers when it is not your turn.';
      }
    }

    let apCost = 0;
    let requiresReaction = false;
    if (summonControl) {
      requiresReaction = controlCost === 'r';
      apCost = typeof controlCost === 'number' ? controlCost : 0;
    } else if (isOpportunityAttack) requiresReaction = true;
    else if (spellLike) {
      const raw = CASTING_TIME_AP[castingTime] ?? 3;
      if (castingTime === 'reaction' || raw === 'r') requiresReaction = true;
      else {
        apCost = raw;
        // The spell template applies the same smallest armed AP override.
        for (const definition of statusRead.definitions) {
          const override = Number(scalar(read.value(definition, 'next_spell_ap_override')));
          if (read.has(definition, 'next_spell_ap_override') && Number.isFinite(override) && override >= 0) apCost = Math.min(apCost, override);
        }
      }
    } else {
      apCost = ['passive_ability', 'free_ability', 'sanguine', 'reaction_ability'].includes(itemType) ? 0
        : ['quick_ability', 'lair_ability', 'trap_ability', 'attack_ability', 'weapon', 'natural_weapon'].includes(itemType) ? 1
          : itemType === 'full_ability' ? 3 : 2;
      requiresReaction = itemType === 'reaction_ability';
    }

    // Wizard v9 next_summon_ap_override (ability/summon.ts — Architect of Emergence): an armed status caps a SUMMON cast's AP, lowest wins
    // (the cast consumes it); a reaction cost is untouched.
    const summonCast = SUMMON_LAYERS.some(layer => read.has(ability, layer));
    if (summonCast && !requiresReaction) {
      for (const definition of statusRead.definitions) {
        const override = Number(scalar(read.value(definition, 'next_summon_ap_override')));
        if (read.has(definition, 'next_summon_ap_override') && Number.isFinite(override) && override >= 0 && Math.trunc(override) < apCost) {
          apCost = Math.trunc(override);
        }
      }
    }
    const flag = requiresReaction ? 'no_reactions' : 'no_actions';
    if ((summonControl && !phpEmpty(read.value(caster, flag)))
        || statusRead.definitions.some(definition => !phpEmpty(read.value(definition, flag)))) {
      return requiresReaction ? 'You cannot use reactions while an active status prevents reactions.'
        : 'You cannot take actions while an active status prevents actions.';
    }
    if (!statusRead.complete) undecided('status-definitions');

    if (inCombat) {
      const reaction = ecScalar('reaction');
      if (requiresReaction && !numeric(reaction)) undecided('reaction-unknown');
      // A CLASS AURA's spent reaction is the stance rule's to word (lane AB2, R-038 — ability/class-aura-cast.ts classAuraCastPrecheck, the
      // twin of janus.php's precheck: re-clicking the active aura is "already your active aura", a swap "takes your reaction"); the
      // ability lane's directReactionRefusal still refuses a malformed class_aura item that is not a stance.
      if (requiresReaction && numeric(reaction) && phpIntval(reaction) === 0 && phpEmpty(read.value(ability, 'class_aura'))) {
        return isOpportunityAttack || itemType === 'reaction_ability'
          ? 'You have already used your reaction this round.'
          : 'You have already used your reaction, and cannot cast this spell.';
      }
      if (String(itemField(caster, 'item_type') ?? '') === 'lair') apCost = Math.min(apCost, 1);
      apCost += throwDrawAp;
      const aps = ecScalar('aps');
      // A spell's AP can be lowered by a status the page cannot read; then the shortfall is not provable.
      const apProvable = (!spellLike && !summonCast) || statusRead.complete;
      if (apCost > 0 && (!apProvable || !numeric(aps))) undecided('ap-unknown');
      if (apProvable && apCost > 0 && numeric(aps) && phpIntval(aps) < apCost) {
        const current = phpIntval(aps);
        return spellLike
          ? `This spell requires ${apCost} AP to cast. You only have ${current} AP remaining.`
          : `You do not have enough Action Points. This ability requires ${apCost} AP, you have ${current}.`;
      }
      // LEGENDARY MAIN-TURN ALLOWANCE (legendary_turn_helpers.php ft_legendary_main_turn_ap_floor_blocks): on its own Main
      // Turn a legendary creature keeps (PCs − 1) AP for its Legendary Turns. The schema signal is the encounter row's
      // turn_phase key (the column exists iff it is compiled); the roster must be held to count the party.
      const encountersBucket = record(encounter!.encounters);
      const roster = encounter!.encounter_creatures_arr;
      if (apProvable && apCost > 0 && numeric(aps) && String(itemField(caster, 'item_type') ?? '') === 'creature' && !phpEmpty(read.value(caster, 'legendary_creature'))
        && encountersBucket && 'turn_phase' in encountersBucket && String(scalar(encountersBucket.turn_phase) ?? '') !== 'legendary'
        && upper(encounterField(encounter!, 'current_encounter_creature_id_turn')) !== '' && upper(encounterField(encounter!, 'current_encounter_creature_id_turn')) === ecId(caster)
        && !Array.isArray(roster)) {
        undecided('legendary-roster');
      }
      if (apProvable && apCost > 0 && numeric(aps) && String(itemField(caster, 'item_type') ?? '') === 'creature' && !phpEmpty(read.value(caster, 'legendary_creature'))
        && encountersBucket && 'turn_phase' in encountersBucket && String(scalar(encountersBucket.turn_phase) ?? '') !== 'legendary'
        && upper(encounterField(encounter!, 'current_encounter_creature_id_turn')) !== '' && upper(encounterField(encounter!, 'current_encounter_creature_id_turn')) === ecId(caster)
        && Array.isArray(roster)) {
        const pcs = roster.filter(row => {
          const participant = record(row);
          const ecRow = record(participant?.encounter_creatures);
          if (!ecRow) return false;
          const type = scalar(record(ecRow.items)?.item_type ?? ecRow.item_type);
          return type === 'player_character' && phpEmpty(scalar(ecRow.encounter_creature_dead)) && phpEmpty(scalar(ecRow.encounter_creature_private))
            && phpEmpty(scalar(ecRow.controller_encounter_creature_id));
        }).length;
        const floor = Math.max(0, pcs - 1);
        if (floor > 0 && phpIntval(aps) - apCost < floor) {
          return `Legendary Action Points are reserved for Legendary Turns — this creature can spend only ${Math.max(0, phpIntval(aps) - floor)} more AP on its Main Turn (${floor} AP held in the Legendary pool).`;
        }
      }
    }
  }

  // requires_in_combat / requires_out_of_combat (L326–343).
  if (!internal && encounter !== null) {
    if (read.has(ability, 'requires_in_combat') && !read.inCombat(encounter, caster)) return 'This ability can only be used IN combat.';
    if (read.has(ability, 'requires_out_of_combat') && read.inCombat(encounter, caster)) return 'This ability can only be used OUT of combat.';
  } else if (!internal && (read.has(ability, 'requires_in_combat') || read.has(ability, 'requires_out_of_combat'))) {
    undecided('combat-state');
  }

  // WEAPON TYPE (L374–425, if_weapon_type_equipped_active): a hand-equipped weapon of a listed type, or a natural weapon
  // when melee is listed. Refused only when the page holds the caster's inventory.
  if (read.entries && read.inventoryField) {
    const required = read.entries(rider ?? ability, 'if_weapon_type_equipped_active').map(entry => upper(layerListEntryUuid(entry))).filter(id => id !== '');
    const carried = required.length ? casterInventory(caster) : null;
    if (required.length && carried === null) undecided('weapon-type-inventory');
    if (required.length && carried !== null) {
      const { inventoryField } = read;
      let met = carried.some(row => HAND_SLOT_IDS.has(upper(inventoryField(row, 'equipped'))) && required.includes(upper(read.value(row, 'weapon_type'))));
      if (!met) {
        const natural = [...read.entries(caster, 'natural_weapon'), ...((): unknown[] => { const derived = record(record(caster.computed)?.creature)?.natural_weapon; return Array.isArray(derived) ? derived : []; })()];
        if (natural.length && required.includes(MELEE_WEAPON_TYPE)) met = true;
      }
      if (!met) return 'You do not have the required weapon type equipped to use this ability.';
    }
  }

  // CHARGE RULES (L427–439, ability_charge_rule_layers_first_failure): the own pool, consumes_ability_charges (floor,
  // range-bound floor), add_charges_to_ability (range-bound ceiling). Names come from the entry or the page catalog.
  if (read.entries) {
    const entriesRead = { entries: read.entries };
    const statusDefinitions = read.statuses(caster).map(status => record(status.status_arr)).filter((d): d is Row => d !== null);
    const spendWaived = (id: string): boolean => statusDefinitions.some(definition =>
      read.entries!(definition, 'ability_charges_not_required').some(entry => upper(layerListEntryUuid(entry)) === id));
    const resolveQty = (raw: unknown, layer: Row, entry: Row, wantedId: string): number => {
      const token = raw === true ? '[x]' : String(raw ?? ''); // PHP loose ==: true matches the first token
      if (token === '[x]') {
        if (hasV1 && numeric(v1)) return phpIntval(v1);
        const level = phpIntval(read.value(layer, 'menta_level') ?? read.value(layer, 'spell_level') ?? 0);
        return level > 0 ? level : 1;
      }
      const charges = entryCharges(entry, wantedId, read);
      if (token === '[max]') return charges ? phpIntval(charges.max) : NaN;
      if (token === '[any]') return charges ? phpIntval(charges.current) : NaN;
      return phpIntval(raw);
    };
    // 1. each layer's own pool, when this use spends it (in combat, or no turn recharge at all)
    for (const layer of layers) {
      const layerAbilityId = layerId(layer);
      if (!read.has(layer, 'ability_charges') || layerAbilityId === '') continue;
      const hasTurnRecharge = read.has(layer, 'recharge_range_begin') && read.has(layer, 'recharge_range_end');
      const spends = !hasTurnRecharge ? true : (encounter !== null ? read.inCombat(encounter, caster) : null);
      if (spends === null) { undecided('charge-pool-combat'); continue; }
      if (!spends || chargesProtected) continue;
      if (!abilityBucketsHeld(caster)) { undecided('charge-pool-bucket'); continue; }
      const layerType = String(itemField(layer, 'item_type') ?? '');
      const own = abilityBucket(caster, layerType, entriesRead).map(entry => record(entry)).find(row => row && entryId(row) === layerAbilityId && (row.passive_conditions_active ?? true) !== false);
      const ownCharges = own ? entryCharges(own, layerAbilityId, read) : null;
      if (own && !ownCharges) { undecided('charge-pool-state'); continue; }
      const reading = ownCharges?.current ?? null;
      if (reading !== null && reading !== undefined && reading !== '' && reading !== 'NA' && phpIntval(reading) <= 0) {
        return 'You do not have enough ability charges to use this ability.';
      }
    }
    // 2. consumes_ability_charges
    for (const layer of layers) {
      if (!read.has(layer, 'consumes_ability_charges')) continue;
      const wanted = upper(read.value(layer, 'consumes_ability_charges'));
      if (!abilityBucketsHeld(caster)) return standDown('charge-consumes-bucket');
      const entry = findBucketEntry(caster, wanted, ABILITY_TYPES, entriesRead);
      if (!entry) {
        const name = layerItemName(wanted, layer, 'consumes_ability_charges', read);
        return name === null ? standDown('charge-consumes-name') : `You do not have this ability: ${name}`;
      }
      // Psi reads FULL out of combat and an out-of-combat spend is skipped (owner 2026-10-16, ability/charges/adjust-charges.ts) — never
      // refuse on a held count the page's out-of-combat pin has not caught up with yet.
      if (wanted === PSI_MECHANIC_ID && encounterInCombat(encounter) === false) continue;
      const qtyRaw = read.has(layer, 'consumes_charges_qty') ? read.value(layer, 'consumes_charges_qty') : null;
      const required = qtyRaw === null ? 1 : resolveQty(qtyRaw, layer, entry, wanted);
      const charges = entryCharges(entry, wanted, read);
      if (!charges || Number.isNaN(required)) return standDown('charge-consumes-state'); // the pool's state is not on the page
      const currentInt = phpIntval(charges.current ?? 0);
      // A waiver rides a status definition; one the page has not resolved could be the waiver.
      if (!chargesProtected && read.statuses(caster).some(status => record(status.status_arr) === null)) return standDown('charge-waiver-status');
      const waived = chargesProtected || spendWaived(wanted);
      const definition = entryDefinition(entry, wanted, read);
      if (!definition) return standDown('charge-consumes-def'); // range_bound_charges lives on the def
      const name = entryName(entry, wanted, read);
      if (read.has(definition, 'range_bound_charges') && currentInt - required < 0 && !waived) {
        return name === null ? standDown('charge-consumes-name') : `Cannot extend below zero charges for a Range Bound Charged Ability: ${name}`;
      }
      if (currentInt < required && !waived) return `This ability requires ${required} ability charge(s). You only have ${currentInt}!`;
      // A protected holder spends nothing, but its [x] INVESTMENT is still bounded by the pool: the same chargeInvestmentBudget its
      // steppers show (reduction-guard.ts), refused with the ordinary shortage wording so engine and sheet agree on the number.
      if (chargesProtected && (qtyRaw === true || String(qtyRaw ?? '') === '[x]')) {
        const budget = chargeInvestmentBudget(caster, currentInt, phpIntval(charges.max ?? 0), read);
        if (required > budget) return `This ability requires ${required} ability charge(s). You only have ${budget}!`;
      }
    }
    // 3. add_charges_to_ability
    for (const layer of layers) {
      if (!read.has(layer, 'add_charges_to_ability')) continue;
      const wanted = upper(read.value(layer, 'add_charges_to_ability'));
      if (!abilityBucketsHeld(caster)) return standDown('charge-add-bucket');
      const entry = findBucketEntry(caster, wanted, ABILITY_TYPES, entriesRead);
      if (!entry) {
        const name = layerItemName(wanted, layer, 'add_charges_to_ability', read);
        return name === null ? standDown('charge-add-name') : `You do not have this ability: ${name}`;
      }
      const toAdd = read.has(layer, 'add_charges_qty') ? resolveQty(read.value(layer, 'add_charges_qty'), layer, entry, wanted) : 1;
      const charges = entryCharges(entry, wanted, read);
      const definition = entryDefinition(entry, wanted, read);
      if (!definition || !charges || Number.isNaN(toAdd)) return standDown('charge-add-state');
      if (read.has(definition, 'range_bound_charges') && phpIntval(charges.current ?? 0) + toAdd > phpIntval(charges.max ?? 0)) {
        const name = entryName(entry, wanted, read);
        return name === null ? standDown('charge-add-name') : `Cannot extend beyond max charges for a Range Bound Charged Ability: ${name}`;
      }
    }
    // MACHINIST ORBS (L444–450, orb_queue.php ft_consume_orb_first_failure): every consume_orb slug must be channeled, per layer.
    for (const layer of layers) {
    const orbCosts: unknown[] = read.entries(layer, 'consume_orb');
    if (orbCosts.length) {
      const rawQueue = scalar(ec.orb_queue);
      let queue: unknown = rawQueue;
      if (typeof rawQueue === 'string' && rawQueue !== '') { try { queue = JSON.parse(rawQueue); } catch { queue = null; } }
      const available: Record<string, number> = {};
      if (Array.isArray(queue)) for (const slug of queue) if (typeof slug === 'string' && slug in ORB_NAMES) available[slug] = (available[slug] ?? 0) + 1;
      for (const entry of orbCosts) {
        const slug = typeof entry === 'string' ? entry : String(record(entry)?.value ?? '');
        const name = ORB_NAMES[slug] ?? (slug !== '' ? slug.charAt(0).toUpperCase() + slug.slice(1) + ' Orb' : 'required orb');
        if (!(slug in ORB_NAMES) || (available[slug] ?? 0) < 1) return `You need a channeled ${name} to use this ability.`;
        available[slug] = (available[slug] ?? 0) - 1;
      }
    }
    }
  } else if (layers.some(layer => ['ability_charges', 'consumes_ability_charges', 'add_charges_to_ability', 'consume_orb'].some(key => read.has(layer, key)))) {
    undecided('charge-readers');
  }

  // Mana / luck deltas (L425–454) — tag-free values only; a formula the browser does not resolve stands down.
  const x = hasV1 && numeric(v1) ? v1 : null;
  for (const [layerKey, scalarKey, noun] of [['change_caster_mana', 'mana', 'Mana'], ['change_caster_luck', 'luck', 'Luck']] as const) {
    for (const layer of layers) {
      if (!read.has(layer, layerKey) || (layerKey === 'change_caster_mana' && read.has(layer, 'consumes_mana_on_hit'))) continue;
      let raw = String(read.value(layer, layerKey) ?? '');
      if (x !== null) raw = raw.split('[x]').join(String(phpIntval(x)));
      const current = ecScalar(scalarKey);
      if (raw.includes('[') || raw === 'v_lost' || !numeric(current)) { undecided(`${scalarKey}-delta-formula`); continue; }
      const rawDelta = phpIntval(resolveFormulaArithmetic(raw));
      const delta = scalarKey === 'mana' ? manaReductionDelta(caster, rawDelta, read) : rawDelta;
      if (Number(current) + delta < 0) {
        return `You do not have enough ${scalarKey} to use this ability. Current ${noun}: ${String(current)}, ${noun} Required: ${delta}`;
      }
    }
  }

  // INVENTORY CHARGES (L486–525): a charged item cast pays its charges from the variable_2 row.
  if (hasV1 && (v1 === 'charged_ability' || v1 === 'charged_spell') && hasV2 && read.inventoryField) {
    const row = cachedInventoryRow(v2);
    if (!row && typeof v2 === 'string' && UUID.test(v2)) return standDown('charged-item-row'); // the server resolves this row; the page cannot
    const rowId = row ? read.inventoryField(row, 'inventory_id') : null;
    const charges = row ? read.value(row, 'current_item_charges') : null;
    if (row && typeof rowId === 'string' && UUID.test(rowId) && charges !== null && charges !== undefined && numeric(charges)) {
      const held = phpIntval(charges);
      const used = -chargeReductionDelta(caster, -(v1 === 'charged_ability' ? phpIntval(read.value(ability, 'ability_activation_charges') ?? 1) : phpIntval(read.value(ability, 'spell_level') ?? 1)), read);
      if (held - used < 0) return `You cannot use this ${iconPh(itemType)} because you have insufficient charges (${held}) and it requires ${used} charges.`;
    }
  }
  // CONSUMABLE UNITS (L528–537): a nested-inventory source with consumes_unit_per_use must have a unit left.
  const consumeSource = hasV1 && v1 === 'nested_inventory_source' ? v2 : input.consumeInventoryId;
  if (consumeSource && read.inventoryField) {
    const row = cachedInventoryRow(consumeSource);
    if (!row && typeof consumeSource === 'string' && UUID.test(consumeSource)) undecided('consumable-row');
    const qty = row ? read.inventoryField(row, 'item_qty') : null;
    const unitUse = row ? input.consumeInventoryUnit || consumesUnitPerUse(row, { ...read, definition: id => read.resolveItem?.(id) ?? null }, ability) : false;
    if (unitUse === null) undecided('consumable-template');
    if (row && unitUse && qty !== null && qty !== undefined && phpIntval(qty) < 1) return 'You have no units left of this item to use.';
  }

  // Live status rows: a COMPILED row with no live statuses ELIDES the bucket entirely (verified on staging 2026-09-16: every compiled caster /
  // creature row without statuses lacks the key, and creature_statuses() / has_status_effect() read that as none) — the same
  // lean elision as the EC scalars, so on a compiled row absence is proof of "no statuses", not an unheld bucket.
  const statusesHeld = record(caster.statuses) !== null || ecCompiled;

  // HIDE (L560–570, stealth_awareness.php ft_stealth_hide_blocked_reason): no campaign → the rule passes; a caster already
  // hidden with no lockout → passes; else the first eligible opposing watcher inside 60 ft with an OPEN zone refuses —
  // the lockout message while the re-hide lockout runs, the clear-view message while a watcher sees a visible caster
  // clearly. The lockout ledger and the campaign clock come from the read-only endpoint; the watcher facts from the
  // scene (S4b). Anything the page did not gather stands the gate down.
  if (!internal && abilityId === HIDE_ABILITY) {
    const lockout = input.stealthLockout;
    if (!lockout) return standDown('stealth-lockout-read'); // the ledger / clock were not read for this cast
    if (lockout.campaign) {
      if (!statusesHeld) return standDown('hide-statuses');
      const hidden = statusIds(caster, read).has(STATUS_HIDDEN);
      if (lockout.active || !hidden) {
        // PHP proceeds without an encounter; the page cannot tell "no encounter" from "not held", so it needs its own row.
        const encounterForHide = input.encounter ? upper(encounterField(input.encounter, 'encounter_id')) : '';
        if (encounterForHide === '' || encounterForHide !== upper(scalar(ec.encounter_id))) return standDown('hide-encounter');
        const scene = input.sceneVerdicts?.hide;
        if (!scene) return standDown('hide-scene');
        if (lockout.active) {
          if (scene.zoneOpen === null) return standDown('hide-scene-watcher');
          if (scene.zoneOpen) {
            return `You were spotted recently — you cannot hide again while a watcher is within ${STEALTH_ZONE_FT} ft (about ${Math.trunc(lockout.minutes)} min remaining, or move out of their zone).`;
          }
        } else {
          if (scene.seenClearly === null) return standDown('hide-scene-watcher');
          if (scene.seenClearly) return STEALTH_CLEAR_VIEW_MESSAGE;
        }
      }
    }
  }

  // HUNKER DOWN (L578–585, directional_cover.php ft_hunker_posture_unavailable_reason): aloft under its own flight above the
  // shape's support floor, else no occluder within reach on a shaped encounter the caster stands inside. Both halves need the
  // shape and the occluder volumes — the scene judges them (S4b); the gate only orders and words the refusal.
  if (!internal && abilityId === HUNKER_ABILITY) {
    const scene = input.sceneVerdicts?.hunker;
    if (!scene) return standDown('hunker-scene');
    if (scene.aloft === null) return standDown('hunker-scene-aloft');
    if (scene.aloft) return HUNKER_ALOFT_MESSAGE;
    if (scene.noCover === null) return standDown('hunker-scene-reach');
    if (scene.noCover) return HUNKER_NO_COVER_MESSAGE;
  }

  // REQUIRED / FORBIDDEN STATUSES (L570–633): a vitality-tier id is read from computed.vitality.v_status, any other from
  // the live status rows. The messages carry catalog names, so an unresolvable name stands down.
  const heldIds = statusIds(caster, read);
  const vitality = record(record(caster.computed)?.vitality);
  // v_status_id_of(): a string id, or a {items:{item_id}} shell. A vitality cluster without a readable tier is unknown, not empty.
  const vStatus = ((): string | null => {
    if (!vitality || !('v_status' in vitality)) return null;
    const raw = vitality.v_status;
    if (typeof raw === 'string') return upper(raw);
    const shellId = scalar(record(record(raw)?.items)?.item_id);
    return typeof shellId === 'string' ? upper(shellId) : null;
  })();
  const hasStatusId = (id: string): boolean | null => {
    if (VITALITY_STATUS_IDS.has(id)) return vStatus === null ? null : vStatus === id;
    return statusesHeld ? heldIds.has(id) : null;
  };
  // Both layers of a composite attack, rider first: a Monk stance attack fired through a weapon names its stance on the
  // rider, so reading only the outer weapon let it resolve outside its stance (owner 2026-10-28: those are unusable).
  for (const statusRow of layers) {
    if (internal || !read.entries || !read.has(statusRow, 'if_caster_has_status_effect_active') || abilityId === HUNKER_ABILITY || abilityId === HIDE_ABILITY) continue;
    const required = read.entries(statusRow, 'if_caster_has_status_effect_active').map(entry => upper(layerListEntryUuid(entry)));
    const verdicts = required.map(hasStatusId);
    if (!verdicts.includes(true)) {
      const names = required.map(id => layerItemName(id, statusRow, 'if_caster_has_status_effect_active', read));
      if (!verdicts.includes(null) && names.every((name): name is string => name !== null)) {
        return `You currently do not have any of the required status effects (${names.join(', ')}) active to use this ability!`;
      }
      // Applicable but undecidable (an unreadable tier, an unheld bucket, an unresolvable name): PHP may refuse HERE with a
      // message the page cannot reproduce, so no later rule may speak for it — the whole gate stands down.
      return standDown(verdicts.includes(null) ? 'status-required' : 'status-required-name');
    }
  }
  if (!internal && read.entries && read.has(ability, 'if_caster_has_status_effect_not_active') && abilityId !== HUNKER_ABILITY && abilityId !== END_HUNKER_ABILITY) {
    for (const entry of read.entries(ability, 'if_caster_has_status_effect_not_active')) {
      const id = upper(layerListEntryUuid(entry));
      const held = hasStatusId(id);
      if (held === false) continue;
      if (held === null) return standDown('status-forbidden'); // undecidable: see above
      const name = itemName(id, read, entry);
      if (name === null) return standDown('status-forbidden-name');
      return `You currently have the status effect ${name} active and CANNOT use this ability!`;
    }
  }

  // CONCENTRATION CAP (L635–670): 1, or 2 with Bifocus (double_concentration, held or feat-granted); skipped on a finish.
  const finishingDelayedCast = hasV1 && ['finish_casting', 'finish_casting_ritual_spell'].includes(String(v1));
  if (!finishingDelayedCast && !(statusesHeld && read.entries && read.resolveItem) && !phpEmpty(read.value(ability, 'spell_concentration'))) undecided('concentration-statuses');
  if (!finishingDelayedCast && statusesHeld && read.entries && read.resolveItem && !phpEmpty(read.value(ability, 'spell_concentration'))) {
    const count = read.statuses(caster).filter(status => {
      const definition = record(status.status_arr);
      return upper(scalar(record(definition?.items)?.item_id) ?? scalar(status.status_item_id)) === STATUS_CONCENTRATING;
    }).length;
    if (count > 0) {
      const bifocus = casterHoldsPassive(caster, PASSIVE_DOUBLE_CONCENTRATION, { entries: read.entries, resolveItem: read.resolveItem });
      if (bifocus === null) return standDown('concentration-bifocus'); // a feat the page cannot read could be Bifocus: undecidable
      if (count >= (bifocus ? 2 : 1)) {
        return `You are already concentrating on ${count} ${count === 1 ? 'spell' : 'spells'}(s) or abilities. End concentration as a reaction before using this ability.`;
      }
    }
  }
  // RITUAL NEEDS CONCENTRATION (L672–683).
  if (!finishingDelayedCast && (itemType === 'spell' || itemType === 'curse') && hasV1 && String(v1) === 'spell_ritual' && !statusesHeld) undecided('ritual-statuses');
  if (!finishingDelayedCast && (itemType === 'spell' || itemType === 'curse') && hasV1 && String(v1) === 'spell_ritual' && heldIds.has(STATUS_CONCENTRATING)) {
    return `Casting a ${iconPh('spell_ritual')} ritual spell requires ${iconPh('concentration')} concentration. You must break your concentration, using your ${iconPh('reaction')} reaction, before casting a ritual spell.`;
  }

  // TOOLS (L686–714) and REAGENTS (L717–763): carried (containers recursed), else on the encounter floor within reach.
  // The page proves "missing" (no carried match and no floor match) and "unresolvable"; a floor match needs the reach
  // geometry (S4) and stands down. A crafting recipe judged as a SIZED plan (crafting_roll, no variable_1) stands down.
  if (!internal && read.entries && read.inventoryField && (read.has(ability, 'requires_tool') || read.has(ability, 'requires_reagent'))) {
    const { inventoryField } = read;
    const carried = casterInventory(caster);
    const floorRaw = encounter?.encounter_inventories_arr ?? caster.encounter_inventory;
    // inventory_field(): a lean row elides item_qty = 1; the page reader returns undefined for it.
    const rowQty = (row: Row): number => { const qty = inventoryField(row, 'item_qty'); return qty == null ? (UUID.test(String(inventoryField(row, 'inventory_id') ?? '')) ? 1 : 0) : phpIntval(qty); };
    const carriedHas = (id: string, qty: number, template: string | null, wholeStock: boolean): boolean | null => {
      if (carried === null) return null;
      return walkInventory(carried).some(row => upper(inventoryField(row, 'inventory_item_id')) === id && rowQty(row) >= qty
        && (template === null || reagentTemplateMatches(inventoryField(row, 'enchantment_template_id'), template))
        && (!wholeStock || phpEmpty(inventoryField(row, 'inventory_size'))) && (!wholeStock || UUID.test(String(inventoryField(row, 'inventory_id') ?? ''))));
    };
    // Floor stacks: 'ok' when one is within reach, 'out_of_reach' when matches exist but none is, 'missing' when none match,
    // null when the page cannot say (rows unheld, or a match whose reach it cannot measure). Contents of a floor container
    // use the container's own position (caster_required_item_resolution walks the outer row).
    const floorRaw2 = Array.isArray(floorRaw) ? floorRaw.flatMap(entry => { const row = record(entry); return row ? [row] : []; }) : null;
    const floorHas = (id: string, qty: number, template: string | null, wholeStock: boolean): 'ok' | 'out_of_reach' | 'missing' | null => {
      if (floorRaw2 === null) return null;
      let matched = false; let unknown = false;
      const visit = (rows: Row[], holder: Row | null): boolean => {
        for (const row of rows) {
          const position = holder ?? row;
          if (phpEmpty(inventoryField(row, 'inventory_deleted')) && upper(inventoryField(row, 'inventory_item_id')) === id && rowQty(row) >= qty
            && (template === null || reagentTemplateMatches(inventoryField(row, 'enchantment_template_id'), template))
            && (!wholeStock || phpEmpty(inventoryField(row, 'inventory_size')))) {
            matched = true;
            const near = withinReach(caster, position, read);
            if (near === true) return true;
            if (near === null) unknown = true;
          }
          const nested = row.inventory_in_container ?? row.container_contents;
          if (Array.isArray(nested) && visit(nested.flatMap(entry => { const child = record(entry); return child ? [child] : []; }), position)) return true;
        }
        return false;
      };
      if (visit(floorRaw2, null)) return 'ok';
      if (unknown) return null;
      return matched ? 'out_of_reach' : 'missing';
    };
    for (const entry of read.entries(ability, 'requires_tool')) {
      if (entry === null || entry === '' || (Array.isArray(entry) && entry.length === 0)) continue;
      const id = upper(inventoryListEntry(entry).uuid);
      if (!UUID.test(id)) return 'This item lists a required tool that could not be resolved — please report this recipe/ability.';
      const inHand = carriedHas(id, 1, null, false);
      if (inHand === null) return standDown('tool-carried');
      if (inHand) continue;
      const onFloor = floorHas(id, 1, null, false);
      if (onFloor === null) return standDown('tool-floor');
      if (onFloor === 'ok') continue;
      const name = itemName(id, read, entry);
      if (name === null) return standDown('tool-name');
      if (onFloor === 'out_of_reach') return `You have ${name} on this encounter, but it must be within reach to use.`;
      return `To complete this task you must have the required tool in your inventory or environment: ${name}`;
    }
    if (read.has(ability, 'requires_reagent')) {
      const totals = new Map<string, { id: string; template: string | null; qty: number }>();
      if (upper(read.value(ability, 'roll_type')) === ROLL_CRAFTING && (!hasV1 || phpEmpty(v1))) {
        // SIZED CRAFT (L737–757): the plan gates on the size-scaled manifest; a null plan is refused outright (S4b).
        const plan = craftingBuildPlan(ability, v2, { ...read, entries: read.entries });
        if (plan === null) return standDown('craft-plan');
        if (plan === 'refuse') return CRAFT_SIZE_MESSAGE;
        // ft_reagent_requirements($recipe, $manifest): the manifest rows are already aggregated and whole.
        for (const row of plan.manifest) totals.set(`${row.id}|${row.template ?? ''}`, { id: row.id, template: row.template, qty: row.qty });
        // crafting_waives_nonmagical_reagents (Wizard v9 "Universal Crafter", 2026-10-09; crafting-waiver.ts): a crafter holding it needs
        // only the MAGICAL requirements (an enchantment template) — every untemplated one is waived. A provider the page cannot resolve
        // stands down only when a waivable requirement exists.
        if ([...totals.values()].some(row => !reagentRequirementIsMagical(row.template))) {
          const waives = craftingWaivesNonmagicalReagents(read, caster);
          if (waives === null) return standDown('reagent-waiver');
          if (waives) for (const [key, row] of [...totals]) if (!reagentRequirementIsMagical(row.template)) totals.delete(key);
        }
      } else {
        for (const entry of read.entries(ability, 'requires_reagent')) {
          if (entry === null || entry === '' || (Array.isArray(entry) && entry.length === 0)) continue;
          const resolved = inventoryListEntry(entry);
          const id = upper(resolved.uuid);
          const template = resolved.template !== null ? upper(resolved.template) : '';
          const rawQty = resolved.rawQty === undefined ? 1 : resolved.rawQty;
          if (!UUID.test(id) || (template !== '' && !UUID.test(template)) || !numeric(rawQty) || !Number.isFinite(Number(rawQty))) return 'This item has a malformed reagent requirement.';
          const qty = Math.max(1, Math.round(Number(rawQty)));
          const key = `${id}|${template}`;
          const slot = totals.get(key) ?? { id, template: template === '' ? null : template, qty: 0 };
          slot.qty += qty;
          totals.set(key, slot);
        }
      }
      for (const requirement of totals.values()) {
        const template = requirement.template ?? '';
        const inHand = carriedHas(requirement.id, requirement.qty, template, true);
        if (inHand === null) return standDown('reagent-carried');
        if (inHand) continue;
        if (encounter === null) return standDown('reagent-encounter');
        let onFloor: 'ok' | 'out_of_reach' | 'missing' | null = 'missing';
        if (!read.inCombat(encounter, caster)) {
          onFloor = floorHas(requirement.id, requirement.qty, template, true);
          if (onFloor === null) return standDown('reagent-floor');
          if (onFloor === 'ok') continue;
        }
        const name = layerItemName(requirement.id, ability, 'requires_reagent', read);
        if (name === null) return standDown('reagent-name');
        if (onFloor === 'out_of_reach') return `You have ${name} on this encounter, but it must be within reach to use.`;
        return `To complete this task you must have the required reagent in your inventory or environment: ${requirement.qty} × ${name}`;
      }
    }
  }

  if (!internal && (read.has(ability, 'requires_tool') || read.has(ability, 'requires_reagent')) && !(read.entries && read.inventoryField)) undecided('tool-readers');

  // HEAD ATTACK once per turn (L766–805): the natural-weapon head row itself. The attack-rider shape resolves a natural
  // weapon through the catalog (weapon-attack.ts owns that lane) and stands down here.
  if (encounter !== null && read.inCombat(encounter, caster) && !read.has(caster, 'multiple_head_attacks') && phpIntval(ecScalar('head_attack')) === 1
    && upper(read.value(ability, 'equipment_slot')) === EQUIPMENT_SLOT_HEAD && upper(read.value(ability, 'weapon_classification')) === WEAPON_CLASS_NATURAL) {
    return 'You can only make an attack with your head once per turn!';
  }
  if (encounter === null && upper(scalar(ec.encounter_id)) !== '' && upper(read.value(ability, 'equipment_slot')) === EQUIPMENT_SLOT_HEAD) undecided('head-attack-combat');

  // NO HEALING ROLLS (L807–818).
  if (!internal && read.has(ability, 'roll_type') && read.has(caster, 'no_healing_rolls') && [ROLL_HEALING, ROLL_REGENERATION].includes(upper(read.value(ability, 'roll_type')))) {
    return 'You cannot heal targets while you have a no healing rolls status effect active.';
  }

  // MEDIAN CHARGE (L820–853): the named ability must sit at exactly half its max charges. Refused only when the caster
  // bucket holds the ability with a numeric max; an ability the page cannot find is left to the cast.
  if (!internal && read.entries && read.has(ability, 'ability_active_if_ability_charge_is_at_median')) {
    const wanted = upper(read.value(ability, 'ability_active_if_ability_charge_is_at_median'));
    // PHP refuses when no entry is found; the page proves absence only from held buckets, and does not port that message.
    let medianFound = false;
    outer: for (const type of ABILITY_TYPES) {
      for (const entry of abilityBucket(caster, type, { entries: read.entries })) {
        const row = record(entry);
        if (!row || entryId(entry) !== wanted) continue;
        const charges = entryCharges(row, wanted, read);
        if (!charges) return standDown('median-state'); // the pool's state is not on the page
        const { current, max } = charges;
        if (!numeric(max) || Number(max) <= 0) continue; // PHP keeps scanning for a usable entry
        const median = Number(max) / 2;
        if (!numeric(current)) return standDown('median-state'); // a page row without its current charge cannot prove the shortfall
        if (Number(current) === median) { medianFound = true; break outer; }
        const name = entryName(row, wanted, read);
        if (name === null) return standDown('median-name');
        return `To use this ability, ${name} must be at the median charge amount: ${median}.`;
      }
    }
    if (!medianFound) return standDown('median-not-found');
  }

  // SPELL LOCK (L856–860).
  if (!internal) {
    const lockReason = spellLockReason(caster, ability, read.has);
    if (lockReason) return lockReason;
  }

  // BINDING EXPERT (L862–872, restrained_teleport_denial.php): a caster Restrained by a creature carrying
  // restrained_by_caster_cannot_teleport may not teleport. Teleport = a positive change_teleport_remaining (a tagged value
  // stands down); the placer is the status's caster (elided = self); the placer's row comes from the held roster.
  {
    const rawTeleport = read.value(ability, 'change_teleport_remaining');
    if (!internal && rawTeleport !== null && rawTeleport !== undefined && rawTeleport !== '') {
      const amount = plainNumber(rawTeleport);
      if (amount === null) return standDown('binding-teleport-amount');
      if (Math.trunc(amount) > 0) { // PHP casts to (int) before the > 0 test
        if (!statusesHeld) return standDown('binding-statuses'); // PHP reads the live status rows; without them the page cannot judge the hold
        const placers = new Set<string>();
        for (const status of read.statuses(caster)) {
          const definition = record(status.status_arr);
          const statusItemId = upper(scalar(status.status_item_id) ?? scalar(record(definition?.items)?.item_id));
          if (statusItemId !== STATUS_RESTRAINED) continue;
          const placer = upper(scalar(status.status_caster_id) ?? scalar(status.status_target_id));
          if (UUID.test(placer)) placers.add(placer);
        }
        if (placers.size) {
          const roster = encounter?.encounter_creatures_arr;
          if (!Array.isArray(roster)) return standDown('binding-roster'); // PHP resolves the roster itself; the page cannot judge without it
          for (const entry of roster) {
            const row = record(entry);
            const rowId = upper(scalar(record(row?.encounter_creatures)?.encounter_creature_id));
            if (!row || !placers.has(rowId)) continue;
            if (!phpEmpty(read.value(row, 'restrained_by_caster_cannot_teleport'))) return 'You cannot teleport out of a hold this creature has bound you in!';
          }
        }
      }
    }
  }

  // COUNTERSPELL LOCKOUT (L875–894): a spell whose circle / sphere maps to a lockout status the caster carries.
  if (!internal && itemType === 'spell' && !read.entries) return standDown('readers');
  if (!internal && itemType === 'spell' && read.entries) {
    for (const layer of ['spell_circle', 'spell_sphere']) {
      for (const entry of read.entries(ability, layer)) {
        const school = upper(layerListEntryUuid(entry));
        const lock = COUNTERSPELL_LOCKS[school];
        if (!lock) continue;
        if (!statusesHeld) return standDown('counterspell-statuses');
        if (heldIds.has(lock.status)) return `Counterspelled! You are locked out of ${lock.name} until the start of your next turn.`;
      }
    }
  }

  // SUMMONS (L897–932): the cap, then an empty user-choice pick.
  if (SUMMON_LAYERS.some(layer => read.has(ability, layer))) {
    const count = ecScalar('count_caster_summons');
    const max = read.value(caster, 'max_summons');
    if (!numeric(count) || !numeric(max)) undecided('summon-cap');
    if (numeric(count) && numeric(max) && Number(count) >= Number(max)) {
      return `You have summoned your maximum amount of summons of <span class='badge bg-dark border'>${phpIntval(max)}</span>, and cannot summon more!`;
    }
  }
  if (read.has(ability, 'user_choice_1_summon')) {
    // get_item_id_type(): a non-uuid is provably not a creature; a uuid is judged only when the page resolves it.
    const notCreature = (value: unknown): boolean | null => {
      const id = String(value ?? '');
      if (!UUID.test(id)) return true;
      const definition = read.resolveItem?.(id) ?? null;
      const type = itemField(definition, 'item_type');
      return definition && typeof type === 'string' && type !== '' ? type !== 'creature' : null;
    };
    const pick1 = input.summonCreatureId ? notCreature(input.summonCreatureId) : hasV1 ? notCreature(v1) : true;
    const pick2 = hasV2 ? notCreature(v2) : true;
    // A level / ritual / pool marker in the other channel must not invalidate the creature.
    if (pick1 === true && pick2 === true) return 'Your summon selection choice is empty!';
    if (pick1 !== false && pick2 !== false) undecided('summon-pick-def');
  }

  // Spell mana (L914–1025). Quick Ritual's provider charge, an unread status (a discount carrier) or an unknown pool leave the
  // price unproven; PHP's server-only skips (a contingency fire, an Arcane Arrow delivery) never ride a browser cast.
  if (!manaProtected && (itemType === 'spell' || itemType === 'curse') && !(hasV1 && MANA_POOL_SKIPS.includes(String(v1))) && !(hasV1 && String(v1) === 'finish_casting' && (!internal || ['at_will_spell', 'innate_spell_per_long_rest', 'charged_spell', 'charged_ability'].includes(String(v2))))
    && Number(scalar(read.value(ability, 'spell_level'))) > 0 && (!manaKnown || input.quickRitual || !statusRead.complete)) undecided('spell-mana');
  if (!manaProtected && (itemType === 'spell' || itemType === 'curse') && manaKnown && !input.quickRitual && statusRead.complete
    && !(hasV1 && MANA_POOL_SKIPS.includes(String(v1))) && !(hasV1 && String(v1) === 'finish_casting' && (!internal || ['at_will_spell', 'innate_spell_per_long_rest', 'charged_spell', 'charged_ability'].includes(String(v2))))) {
    const levelRaw = read.value(ability, 'spell_level');
    const level = Number(scalar(levelRaw));
    const discount = nextSpellManaDiscountCarriers(read, caster, ability).reduce((n, carrier) => n + carrier.discount, 0);
    const mana = Number(manaRaw);
    // Wizard v9 spell_mana_discount (caster-spell-layers.ts): a CERTAIN discount — summed across distinct in-scope providers — lowers
    // the cost itself (base or cast level, floor 0), together with the held one-use discounts. An unresolvable provider leaves it unproven.
    // Walked only when the undiscounted cost is short (the only case the discount can change a verdict).
    const castLevel = input.castLevel ?? (hasV1 && numeric(v1) ? Number(v1) : null);
    const short = Number.isFinite(level) && level > 0 && (mana - level < 0 || (castLevel !== null && mana - castLevel < 0));
    const fixed = itemType === 'spell' && short ? spellManaDiscount(read, caster, ability) : 0;
    if (fixed === undefined) undecided('spell-mana-discount-provider');
    const cost = (raw: number): number => discountedSpellMana(raw, (fixed ?? 0) + discount);
    // PHP's trailing variable_1/2 clause is always true once the pool-skip markers above are excluded.
    if (Number.isFinite(level) && level > 0 && mana - cost(level) < 0) {
      return `You do not have enough mana to cast this spell.  <br />Current mana: ${String(manaRaw)} <br />Mana Required: ${String(cost(level))}`;
    }
    if (Number.isFinite(level) && level > 0 && castLevel !== null && mana - cost(castLevel) < 0) {
      return `You do not have enough mana to cast this spell.  <br />Current mana: ${String(manaRaw)} <br />Mana Required: ${String(cost(castLevel))}`;
    }
  }

  // check_spell_components (L1029) — PHP's last gate.
  if (!internal && read.entries && read.resolveItem) {
    const components = spellComponentRefusal(ability, caster, input.variable1, input.variable2,
      { has: read.has, entries: read.entries, statuses: read.statuses, resolveItem: read.resolveItem }, undecided);
    if (components) return components.message;
  } else if (!internal && (itemType === 'spell' || itemType === 'curse')) {
    undecided('component-readers');
  }
  return allowed();
}
